import React, { useState, useEffect, useCallback } from 'react';
import { complianceCoverageStyle } from '../services/trustBands';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  Loader, CheckCircle, AlertCircle, XCircle, Plus, Eye,
  RefreshCw, Download, Shield, FileCheck, BarChart3, TrendingUp,
  Bell, GitBranch, Wrench, Map as MapIcon, BookOpen, Bot, Clock, ChevronRight,
  Activity, Target, AlertTriangle, ArrowUpRight, Search, Settings,
  CheckSquare, Square, Zap, FileText, Award, ChevronDown, ChevronUp,
  Filter, X, Edit2, Trash2, Copy, Check, Briefcase, Mail, Send,
  Inbox, ShieldCheck, ArrowRight, ExternalLink, Sparkles, Globe2,
} from 'lucide-react';
import { Modal } from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import * as api from '../services/api';
/* G104 — full v2 redesign of ComplianceCenterPage. Replaces 100vh
 * flex layout + custom gradient tab bar with V2PageShell + V2TabNav.
 * v1 hooks/handlers/api calls preserved verbatim. Scope toggle stays
 * as a v1 segmented control (3 buttons) but reskinned to v2 muted bg. */
import {
  V2PageShell,
  TabNav as V2TabNav,
  Pill as V2Pill,
} from '../components/v2';

/* ══════════════════════════════════════════════════════════════════════════════
   DESIGN SYSTEM — Credo.ai-inspired Enterprise Governance Aesthetic
   Matches PolicyConfigPage for a unified experience
   ══════════════════════════════════════════════════════════════════════════════ */

const BRAND = {
  primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1',
  glow: 'rgba(226, 74, 58, 0.06)',
  mid: '#d4453a', soft: '#f8d7d4', muted: '#eab3ad', tint: '#fff5f4',
  gradient: 'linear-gradient(135deg, #0d9488 0%, #0a766b 50%, #a03025 100%)',
  gradientSubtle: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
};

const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};

const ACCENT = {
  emerald:  { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:    { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:      { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:     { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:   { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const SECTOR_COLORS = {
  FINANCE: '#0d9488', INSURANCE: '#0a766b', HEALTHCARE: '#d97706',
  AI_SPECIFIC: '#059669', SECURITY: '#0891b2', GENERAL: '#6366f1', EMPLOYMENT: '#8b5cf6',
};

const priorityColors = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#f59e0b', LOW: '#6b7280' };

const TABS = [
  { key: 'DASHBOARD', label: 'Dashboard', icon: BarChart3, desc: 'Compliance posture overview and heatmaps' },
  { key: 'ATTESTATIONS', label: 'Attestations', icon: FileCheck, desc: 'Evidence collection and manual control sign-off' },
  { key: 'REMEDIATION', label: 'Remediation', icon: Wrench, desc: 'Track and resolve compliance gaps' },
  { key: 'CONTROL_MAPPING', label: 'Control Mapping', icon: MapIcon, desc: 'Cross-framework canonical control alignment' },
  { key: 'REG_CHANGES', label: 'Reg. Changes', icon: GitBranch, desc: 'Regulatory update tracking and impact analysis' },
  { key: 'REPORTS', label: 'Reports', icon: FileText, desc: 'Generate compliance reports and audit packages' },
  { key: 'AI_ASSISTANT', label: 'AI Assistant', icon: Bot, desc: 'Ask questions about your compliance posture' },
  { key: 'NOTIFICATIONS', label: 'Notifications', icon: Bell, desc: 'Alerts, reminders, and preferences' },
  { key: 'CERTIFICATES', label: 'Certificates', icon: Award, desc: 'Framework compliance certifications' },
];

/* ─── Shared Base Styles ───────────────────────────────────────────────── */
const btnBase = {
  border: 'none', cursor: 'pointer', borderRadius: 8, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease',
};
const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s',
};

/* ─── CSS Keyframes (injected once) ─────────────────────────────────────── */
const GlobalStyles = () => (
  <style>{`
    @keyframes ccSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes ccPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes ccSlideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes ccShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes ccFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .cc-card:hover { border-color: ${BRAND.primary}30 !important; box-shadow: 0 4px 20px ${BRAND.glow}, 0 1px 3px rgba(0,0,0,0.04) !important; transform: translateY(-1px); }
    .cc-card { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .cc-btn-ghost:hover { background: ${S[100]} !important; }
    .cc-btn-primary:hover { filter: brightness(1.08); box-shadow: 0 2px 8px rgba(226,74,58,0.25); }
    .cc-stat-card:hover { border-color: ${S[300]} !important; box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
    .cc-tab:hover { color: ${S[700]} !important; background: ${S[50]} !important; }
    .cc-search:focus { border-color: ${BRAND.primary}60 !important; box-shadow: 0 0 0 3px ${BRAND.glow} !important; }
    .cc-row:hover { background: ${S[25]} !important; }
    .cc-action-btn:hover { background: ${S[150]} !important; color: ${S[800]} !important; }
    .cc-scope-btn:hover { background: ${S[200]} !important; }
  `}</style>
);

/* ══════════════════════════════════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ══════════════════════════════════════════════════════════════════════════════ */

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px' }}>
      <Loader size={24} style={{ animation: 'ccSpin 1s linear infinite', color: BRAND.primary }} />
    </div>
  );
}

function CCCard({ children, style, accent, onClick, className = '' }) {
  return (
    <div className={`cc-card ${className}`} onClick={onClick} style={{
      background: '#fff', border: `1px solid ${S[200]}`, borderRadius: 12,
      position: 'relative', overflow: 'hidden', cursor: onClick ? 'pointer' : 'default',
      ...style,
    }}>
      {accent && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accent, opacity: 0.2 }} />}
      {children}
    </div>
  );
}

function StatCard({ label, value, subtitle, icon: Icon, accent, loading: isLoading }) {
  const a = accent || ACCENT.slate;
  return (
    <div className="cc-stat-card" style={{
      flex: 1, minWidth: 160, background: '#fff', border: `1px solid ${S[200]}`,
      borderRadius: 12, padding: '18px 20px', position: 'relative', overflow: 'hidden',
      cursor: 'default', transition: 'all 0.2s ease',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.15 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 8,
          }}>{label}</div>
          {isLoading ? (
            <div style={{ width: 48, height: 28, borderRadius: 6, background: `linear-gradient(90deg, ${S[100]} 25%, ${S[50]} 50%, ${S[100]} 75%)`, backgroundSize: '200% 100%', animation: 'ccShimmer 1.5s infinite' }} />
          ) : (
            <div style={{ fontSize: 28, fontWeight: 800, color: S[900], lineHeight: 1, letterSpacing: '-0.03em', fontFeatureSettings: "'tnum'" }}>{value}</div>
          )}
          {subtitle && <div style={{ fontSize: 11, color: S[400], marginTop: 6, lineHeight: 1.4 }}>{subtitle}</div>}
        </div>
        {Icon && (
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: a.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${a.border}`,
          }}>
            <Icon size={18} color={a.icon} />
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, size = 'default' }) {
  const map = {
    VALID: ACCENT.emerald, COMPLETED: ACCENT.emerald, APPROVED: ACCENT.emerald, RESOLVED: ACCENT.emerald, VERIFIED: ACCENT.emerald, HEALTHY: ACCENT.emerald,
    EXPIRED: ACCENT.slate, PENDING: ACCENT.amber, CREATED: ACCENT.amber, IN_PROGRESS: ACCENT.sky, IN_REVIEW: ACCENT.violet,
    ASSIGNED: ACCENT.violet, REJECTED: ACCENT.rose, REVOKED: ACCENT.rose, FAILED: ACCENT.rose, OVERDUE: ACCENT.rose,
    OPEN: ACCENT.amber, ACKNOWLEDGED: ACCENT.sky,
  };
  const a = map[status] || ACCENT.slate;
  const isSmall = size === 'small';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSmall ? 4 : 5,
      fontSize: isSmall ? 10 : 11, fontWeight: 600,
      padding: isSmall ? '2px 8px' : '3px 10px',
      borderRadius: 6, background: a.bg, color: a.text,
      border: `1px solid ${a.border}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
      letterSpacing: '0.01em',
    }}>
      {status}
    </span>
  );
}

function Badge({ text, color, bg }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', fontSize: 10, fontWeight: 600,
      padding: '2px 8px', borderRadius: 6,
      background: bg || S[100], color: color || S[600],
      border: `1px solid ${S[200]}`,
      fontFamily: "'IBM Plex Mono', 'SF Mono', monospace",
    }}>{text}</span>
  );
}

function PriorityBadge({ priority }) {
  const map = { CRITICAL: ACCENT.rose, HIGH: ACCENT.amber, MEDIUM: ACCENT.amber, LOW: ACCENT.slate };
  const a = map[priority] || ACCENT.slate;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      background: a.bg, color: a.text, border: `1px solid ${a.border}`,
    }}>
      <AlertTriangle size={11} />
      {priority}
    </span>
  );
}

function EmptyState({ icon: Icon, title, subtitle, children }) {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 20px',
      animation: 'ccFadeIn 0.3s ease',
    }}>
      <div style={{
        width: 56, height: 56, borderRadius: 16, background: S[100],
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        {Icon && <Icon size={24} color={S[400]} />}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: S[700], marginBottom: 6 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12, color: S[500], lineHeight: 1.5 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

function SubViewToggle({ views, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, background: S[100], borderRadius: 10, padding: 3, marginBottom: 20 }}>
      {views.map(v => {
        const Icon = v.icon;
        const isActive = active === v.key;
        return (
          <button key={v.key} onClick={() => onChange(v.key)} style={{
            ...btnBase, padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: isActive ? 700 : 500,
            background: isActive ? '#fff' : 'transparent',
            color: isActive ? BRAND.primary : S[500],
            boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
          }}>
            <Icon size={13} /> {v.label}
            {v.count != null && v.count > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                background: isActive ? BRAND.light : S[200],
                color: isActive ? BRAND.primary : S[500],
              }}>{v.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function DataTable({ columns, rows, emptyMsg = 'No data available' }) {
  if (!rows || rows.length === 0) {
    return <EmptyState icon={Search} title={emptyMsg} />;
  }
  const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: S[400],
    textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${S[200]}`, background: S[50],
  };
  const tdStyle = { padding: '10px 12px', fontSize: 13, borderBottom: `1px solid ${S[100]}` };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{columns.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className="cc-row" style={{ transition: 'background 0.1s' }}>
              {columns.map(c => <td key={c.key} style={tdStyle}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB 1: DASHBOARD
   ══════════════════════════════════════════════════════════════════════════════ */
function DashboardTab({ agentScope, agentType }) {
  const [data, setData] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { session } = useAuth();
  const { addToast } = useToast();

  useEffect(() => {
    async function fetchData() {
      try {
        const [dashRes, heatRes] = await Promise.all([
          api.ccGetDashboard(agentScope),
          api.ccGetHeatmap(session?.tenantId, agentType),
        ]);
        if (dashRes.ok) setData(dashRes.data);
        if (heatRes.ok) setHeatmapData(heatRes.data);
      } catch (e) { addToast('Failed to load dashboard', 'error'); }
      setLoading(false);
    }
    fetchData();
  }, [agentScope, agentType, session?.tenantId, addToast]);

  if (loading) return <LoadingSpinner />;
  if (!data) return <EmptyState icon={BarChart3} title="No compliance data" subtitle="Configure compliance frameworks and assign controls to see your dashboard" />;

  const score = data.overallScore || 0;
  // Framework coverage, not an agent's conduct — so it keeps its own 80 / 60 thresholds and only
  // the colour comes from the shared ramp. Folding it onto the autonomy ladder would restate what
  // the number means.
  const coverage = complianceCoverageStyle(data.overallScore);
  const scoreColor = coverage.ink;
  const scoreAccent = coverage.accent;   // { bg, text, border, icon } — the shape this page uses
  const circumference = 2 * Math.PI * 54;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      {/* Executive hero row: circular gauge + key stats */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24, alignItems: 'stretch' }}>
        <CCCard accent={scoreColor} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 200, padding: 28 }}>
          <svg width="130" height="130" viewBox="0 0 130 130" style={{ transform: 'rotate(-90deg)' }}>
            <circle cx="65" cy="65" r="54" fill="none" stroke={S[200]} strokeWidth="10" />
            <circle cx="65" cy="65" r="54" fill="none" stroke={scoreColor} strokeWidth="10"
              strokeDasharray={circumference} strokeDashoffset={dashOffset}
              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
          </svg>
          <div style={{ position: 'relative', marginTop: -90, marginBottom: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: scoreColor, letterSpacing: '-0.03em' }}>{score.toFixed(1)}%</div>
            <div style={{ fontSize: 10, color: S[500], fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Compliance</div>
          </div>
          <div style={{
            fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 8,
            background: scoreAccent.bg, color: scoreAccent.text,
            border: `1px solid ${scoreAccent.border}`,
          }}>
            {score >= 80 ? 'Healthy' : score >= 60 ? 'Needs Attention' : 'Critical'}
          </div>
        </CCCard>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <StatCard label="Frameworks Tracked" value={data.frameworksTracked || 0} icon={Shield} accent={ACCENT.sky} />
          <StatCard label="Total Controls" value={data.totalControls || 0} icon={FileCheck} accent={ACCENT.slate} />
          <StatCard label="Passing" value={data.passing || 0} icon={CheckCircle} accent={ACCENT.emerald} />
          <StatCard label="Failing" value={data.failing || 0} icon={XCircle} accent={ACCENT.rose} />
          <StatCard label="Open Remediations" value={data.openRemediations || 0} icon={Wrench} accent={ACCENT.amber} />
          <StatCard label="Overdue Attestations" value={data.overdueAttestations || 0} icon={Clock} accent={ACCENT.rose} />
        </div>
      </div>

      {/* Heatmap */}
      {heatmapData && (heatmapData.agents || []).length > 0 && (
        <CCCard accent={BRAND.primary} style={{ marginBottom: 20, padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, background: BRAND.gradientSubtle,
                border: `1px solid ${S[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BarChart3 size={14} color={BRAND.primary} />
              </div>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: S[900], margin: 0 }}>Framework Compliance Heatmap</h3>
            </div>
            <div style={{ fontSize: 11, color: S[500], display: 'flex', gap: 12 }}>
              {[
                { label: 'Pass', a: ACCENT.emerald },
                { label: 'Review', a: ACCENT.amber },
                { label: 'Fail', a: ACCENT.rose },
                { label: 'N/A', a: { bg: S[100], border: S[200] } },
              ].map(l => (
                <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: l.a.bg, border: `1px solid ${l.a.border}` }} /> {l.label}
                </span>
              ))}
            </div>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${S[200]}` }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left', position: 'sticky', left: 0, background: S[50], zIndex: 1, padding: '10px 12px', borderBottom: `1px solid ${S[200]}` }}>Agent</th>
                  {(heatmapData.frameworks || []).map(f => (
                    <th key={f} style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center', minWidth: 70, padding: '10px 12px', borderBottom: `1px solid ${S[200]}` }}>{f}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(heatmapData.agents || []).map((agentName, idx) => {
                  const agentMeta = (heatmapData.agentMeta || [])[idx] || {};
                  const rowData = (heatmapData.matrix || {})[agentName] || {};
                  return (
                    <tr key={agentName} className="cc-row">
                      <td style={{ fontWeight: 600, fontSize: 12, position: 'sticky', left: 0, background: '#fff', zIndex: 1, padding: '10px 12px', borderBottom: `1px solid ${S[100]}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: 6,
                            background: agentMeta.type === 'NATIVE' ? ACCENT.violet.bg : ACCENT.sky.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${agentMeta.type === 'NATIVE' ? ACCENT.violet.border : ACCENT.sky.border}`,
                          }}>
                            <Bot size={12} color={agentMeta.type === 'NATIVE' ? ACCENT.violet.icon : ACCENT.sky.icon} />
                          </div>
                          <span style={{ color: S[800] }}>{agentName}</span>
                          <Badge text={agentMeta.type || 'NATIVE'} color={agentMeta.type === 'NATIVE' ? ACCENT.violet.text : ACCENT.sky.text} bg={agentMeta.type === 'NATIVE' ? ACCENT.violet.bg : ACCENT.sky.bg} />
                        </div>
                      </td>
                      {(heatmapData.frameworks || []).map(fw => {
                        const status = rowData[fw] || 'na';
                        const a = status === 'pass' ? ACCENT.emerald : status === 'warn' ? ACCENT.amber : status === 'fail' ? ACCENT.rose : { bg: S[100], text: S[400] };
                        const symbol = status === 'pass' ? '✓' : status === 'warn' ? '!' : status === 'fail' ? '✗' : '—';
                        return (
                          <td key={fw} style={{
                            textAlign: 'center', background: a.bg, color: a.text, fontWeight: 700,
                            padding: '10px 4px', borderBottom: `1px solid ${S[100]}`, fontSize: 13,
                          }}>{symbol}</td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CCCard>
      )}

      {(!heatmapData || (heatmapData.agents || []).length === 0) && (
        <CCCard style={{ marginBottom: 20, padding: 40, textAlign: 'center' }}>
          <BarChart3 size={32} style={{ color: S[300], marginBottom: 10 }} />
          <div style={{ fontSize: 14, fontWeight: 700, color: S[700] }}>No heatmap data available</div>
          <div style={{ fontSize: 12, marginTop: 4, color: S[500] }}>Configure compliance for agents to see the framework heatmap</div>
        </CCCard>
      )}

      {/* Bottom cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        <CCCard accent={ACCENT.amber.icon} style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <AlertTriangle size={15} color={ACCENT.amber.icon} />
            <h3 style={{ fontSize: 13, fontWeight: 700, color: S[900], margin: 0 }}>Agents Requiring Attention</h3>
          </div>
          {(data.agentsAtRisk || []).length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.agentsAtRisk.map(a => (
                <div key={a.id} style={{
                  padding: '10px 14px', background: ACCENT.amber.bg, borderRadius: 8,
                  border: `1px solid ${ACCENT.amber.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: S[800] }}>{a.name}</span>
                  <Badge text={`${a.failingControls} failing`} color={ACCENT.rose.text} bg={ACCENT.rose.bg} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={CheckCircle} title="All agents compliant" />
          )}
        </CCCard>

        <CCCard accent={BRAND.primary} style={{ padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Activity size={15} color={BRAND.primary} />
            <h3 style={{ fontSize: 13, fontWeight: 700, color: S[900], margin: 0 }}>Control Coverage</h3>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            {[
              { label: 'Automated', value: Math.round((data.automatedCount || 0) / (data.totalControls || 1) * 100), color: BRAND.primary, accent: ACCENT.rose },
              { label: 'Manual', value: Math.round((data.manualCount || 0) / (data.totalControls || 1) * 100), color: ACCENT.violet.icon, accent: ACCENT.violet },
            ].map(m => (
              <div key={m.label} style={{
                flex: 1, textAlign: 'center', padding: '16px 12px', borderRadius: 10,
                background: m.accent.bg, border: `1px solid ${m.accent.border}`,
              }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: m.color, letterSpacing: '-0.03em' }}>{m.value}%</div>
                <div style={{ fontSize: 11, color: m.accent.text, marginTop: 4, fontWeight: 600 }}>{m.label}</div>
              </div>
            ))}
          </div>
        </CCCard>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 2: ATTESTATIONS
   ══════════════════════════════════════════════════════════════════════════════ */
/* ── Evidence type icon helper ── */
const EVIDENCE_ICONS = {
  DOCUMENT: FileText, SCREENSHOT: Eye, LOG_EXPORT: Activity,
  ATTESTATION_FORM: CheckSquare, EXTERNAL_LINK: ExternalLink, MULTIPLE: Copy,
  FORM: CheckSquare, LINK: ExternalLink,
};

/* ── Attestation detail row ── */
const AttDetailRow = ({ label, value, mono, accent }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12 }}>
      <span style={{ fontWeight: 600, color: S[500], minWidth: 120, flexShrink: 0 }}>{label}</span>
      <span style={{
        color: accent || S[800],
        fontFamily: mono ? "'Consolas', monospace" : 'inherit',
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );
};

/* ── Evidence item card ── */
const EvidenceItem = ({ item, index, taskId, onDelete, readOnly }) => {
  const EIcon = EVIDENCE_ICONS[item.evidenceType] || FileText;
  const isFile = item.contentBase64 || item.storagePath;
  const isLink = item.evidenceType === 'EXTERNAL_LINK' || item.evidenceType === 'LINK' || item.externalUrl;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
      background: '#fff', borderRadius: 6, border: `1px solid ${S[200]}`,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 6, background: ACCENT.sky.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <EIcon size={13} color={ACCENT.sky.icon} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: S[800], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.fileName || item.description || item.externalUrl || `Evidence ${index + 1}`}
        </div>
        <div style={{ fontSize: 10, color: S[400], display: 'flex', gap: 8, marginTop: 1 }}>
          <span>{item.evidenceType || 'DOCUMENT'}</span>
          {item.uploadedAt && <span>· {new Date(item.uploadedAt).toLocaleDateString()}</span>}
          {item.fileSizeBytes && <span>· {(item.fileSizeBytes / 1024).toFixed(0)} KB</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        {isLink && item.externalUrl && (
          <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" title="Open link"
            style={{ padding: 4, borderRadius: 4, color: ACCENT.sky.icon }}>
            <ExternalLink size={13} />
          </a>
        )}
        {isFile && (
          <button onClick={() => window.open(`/api/v1/compliance-center/attestations/${taskId}/evidence/${item.evidenceId || item.id}/download`)}
            title="Download" style={{ ...btnBase, padding: 4, background: 'transparent', color: S[500] }}>
            <Download size={13} />
          </button>
        )}
        {!readOnly && onDelete && (
          <button onClick={() => onDelete(item.evidenceId || item.id)}
            title="Remove" style={{ ...btnBase, padding: 4, background: 'transparent', color: ACCENT.rose.icon }}>
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

/* ── AI Analysis badge ── */
const AIBadge = ({ att }) => {
  if (!att.aiPreFilled && !att.aiRecommendation) return null;
  const recColors = {
    APPROVE: ACCENT.emerald, NEEDS_REVIEW: ACCENT.amber,
    INSUFFICIENT: ACCENT.rose, NO_EVIDENCE: ACCENT.slate,
  };
  const rc = recColors[att.aiRecommendation] || ACCENT.slate;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 10, fontSize: 10, fontWeight: 600,
      background: rc.bg, color: rc.text, border: `1px solid ${rc.border}`,
    }}>
      <Sparkles size={10} />
      AI: {att.aiRecommendation || 'Analyzed'}
      {att.aiConfidenceScore != null && <span style={{ opacity: 0.7 }}>({(att.aiConfidenceScore * 100).toFixed(0)}%)</span>}
    </div>
  );
};

/* ── Phase 9: Attestation re-assignment row ──
   Inline picker shown next to "Assigned To" when the current user can
   re-assign and the task isn't terminal. Lists tenant users. The actual
   write goes through PUT /api/v1/compliance-center/attestations/{id}
   (api.ccUpdateAttestation), which already supports reassignment. */
function ReassignmentRow({ att, isReassignable, onReassigned, addToast }) {
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pickedUserId, setPickedUserId] = useState(att.assignedTo || '');
  const [saving, setSaving] = useState(false);

  // Lazy-load users only when the operator opens the picker — keeps the
  // attestation list lightweight.
  useEffect(() => {
    if (!editing || users.length > 0) return;
    let cancelled = false;
    setLoadingUsers(true);
    (async () => {
      try {
        const res = await api.getUsers({});
        if (cancelled) return;
        if (res.ok) {
          const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
          setUsers(list);
        }
      } catch (_) { /* swallow — picker stays empty */ }
      finally { if (!cancelled) setLoadingUsers(false); }
    })();
    return () => { cancelled = true; };
  }, [editing, users.length]);

  const save = async () => {
    if (!pickedUserId || pickedUserId === att.assignedTo) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await api.ccUpdateAttestation(att.id, { assignedTo: pickedUserId });
      if (res.ok) {
        addToast('Attestation re-assigned', 'success');
        setEditing(false);
        onReassigned();
      } else {
        addToast(res.error || 'Re-assignment failed', 'error');
      }
    } catch (e) {
      addToast(e.message || 'Re-assignment failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const currentName = att.assigneeName || att.assignee || '—';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
      borderBottom: `1px solid ${S[100]}`,
    }}>
      <span style={{ fontSize: 11, color: S[500], minWidth: 90, fontWeight: 600 }}>
        Assigned To
      </span>
      {!editing ? (
        <>
          <span style={{ fontSize: 13, color: S[800], flex: 1 }}>{currentName}</span>
          {isReassignable && (
            <button
              onClick={() => setEditing(true)}
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 6,
                border: `1px solid ${S[200]}`, background: '#fff', color: S[600],
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
              title="Re-assign this attestation to a different user"
            >
              <Edit2 size={11} /> Re-assign
            </button>
          )}
        </>
      ) : (
        <>
          <select
            value={pickedUserId}
            onChange={e => setPickedUserId(e.target.value)}
            disabled={loadingUsers || saving}
            style={{
              flex: 1, fontSize: 12, padding: '4px 8px', borderRadius: 6,
              border: `1px solid ${S[200]}`, fontFamily: 'inherit', outline: 'none',
            }}
          >
            <option value="">{loadingUsers ? 'Loading users…' : 'Select assignee…'}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}{u.role ? ` (${u.role})` : ''}
              </option>
            ))}
          </select>
          <button onClick={save} disabled={saving || !pickedUserId} style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 6, border: 'none',
            background: saving ? S[300] : ACCENT.emerald.icon, color: '#fff',
            cursor: saving ? 'wait' : 'pointer',
          }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setPickedUserId(att.assignedTo || ''); }}
                  disabled={saving}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 6,
                    border: `1px solid ${S[200]}`, background: '#fff', color: S[600], cursor: 'pointer',
                  }}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

function AttestationsTab() {
  const [subView, setSubView] = useState('MY_QUEUE');
  const [attestations, setAttestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [evidenceItems, setEvidenceItems] = useState({}); // taskId → evidence array
  const [loadingEvidence, setLoadingEvidence] = useState({});
  const [submitForm, setSubmitForm] = useState({ notes: '' });
  const [reviewForm, setReviewForm] = useState({ notes: '', rejectionReason: '' });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  // ── Phase 5 — Auto-Evidenced Controls sub-view state ────────────────
  // Reads PolicyAssignmentActivation snapshots from /api/v1/policy/activations
  // and renders them as a timeline. Each row shows the rules + bindings +
  // attestation tasks that the unified wizard's Stage 5 atomic commit
  // produced, plus the framework binding it satisfied. Click-through
  // navigates to the existing attestation card via subView=MY_QUEUE.
  const [activations, setActivations] = useState([]);
  const [activationsLoading, setActivationsLoading] = useState(false);
  const [expandedActivationId, setExpandedActivationId] = useState(null);
  const { addToast } = useToast();
  const { user } = useAuth();

  // ── Data loading ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (subView === 'MY_QUEUE') {
        res = await api.ccGetMyAttestationQueue();
      } else if (subView === 'REVIEW_QUEUE') {
        res = await api.ccGetReviewQueue();
      } else if (subView === 'ASSIGNED_BY_ME') {
        res = await api.ccListAttestations({});
      } else if (subView === 'HISTORY') {
        const [approvedRes, rejectedRes, cancelledRes, expiredRes] = await Promise.all([
          api.ccListAttestations({ status: 'APPROVED' }),
          api.ccListAttestations({ status: 'REJECTED' }),
          api.ccListAttestations({ status: 'CANCELLED' }),
          api.ccListAttestations({ status: 'EXPIRED' }),
        ]);
        const merged = [
          ...(Array.isArray(approvedRes.data) ? approvedRes.data : approvedRes.data?.content || []),
          ...(Array.isArray(rejectedRes.data) ? rejectedRes.data : rejectedRes.data?.content || []),
          ...(Array.isArray(cancelledRes.data) ? cancelledRes.data : cancelledRes.data?.content || []),
          ...(Array.isArray(expiredRes.data) ? expiredRes.data : expiredRes.data?.content || []),
        ].sort((a, b) => new Date(b.updatedAt || b.reviewedAt || 0) - new Date(a.updatedAt || a.reviewedAt || 0));
        setAttestations(merged);
        setLoading(false);
        return;
      } else {
        res = await api.ccListAttestations({});
      }
      if (res.ok) setAttestations(Array.isArray(res.data) ? res.data : res.data?.content || []);
    } catch (e) { addToast('Failed to load attestations', 'error'); }
    setLoading(false);
  }, [subView, addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Phase 5 — load activations only when the AUTO_EVIDENCED sub-view
  //   is active. Kept separate from loadData() so the existing 4 sub-views
  //   are completely untouched (no regression risk).
  const loadActivations = useCallback(async () => {
    setActivationsLoading(true);
    try {
      const res = await api.listPolicyActivations({ limit: 50 });
      if (res.ok) {
        const list = Array.isArray(res.data?.activations) ? res.data.activations : [];
        setActivations(list);
      }
    } catch (e) { addToast('Failed to load activations', 'error'); }
    setActivationsLoading(false);
  }, [addToast]);

  useEffect(() => {
    if (subView === 'AUTO_EVIDENCED') loadActivations();
  }, [subView, loadActivations]);

  // ── Load evidence when a card is expanded ──
  const loadEvidence = useCallback(async (taskId, forceReload = false) => {
    if (!forceReload && evidenceItems[taskId]) return; // already loaded
    setLoadingEvidence(prev => ({ ...prev, [taskId]: true }));
    try {
      const res = await api.ccGetEvidence(taskId);
      if (res.ok) {
        const items = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.evidencePayload || []);
        setEvidenceItems(prev => ({ ...prev, [taskId]: items }));
      }
    } catch (e) { /* silent */ }
    setLoadingEvidence(prev => ({ ...prev, [taskId]: false }));
  }, [evidenceItems]);

  const handleExpand = (att) => {
    const newId = expandedId === att.id ? null : att.id;
    setExpandedId(newId);
    if (newId) loadEvidence(att.id);
  };

  // ── Evidence upload ──
  const handleUploadEvidence = async (taskId, file) => {
    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      const res = await api.ccUploadEvidence(taskId, {
        fileName: file.name,
        evidenceType: 'DOCUMENT',
        description: file.name,
        contentBase64: base64,
        mimeType: file.type,
      });
      if (res.ok) {
        addToast('Evidence uploaded', 'success');
        loadEvidence(taskId, true); // force reload evidence list
      } else {
        addToast(res.data?.message || 'Evidence upload failed', 'error');
      }
    } catch (e) {
      addToast('Upload failed: ' + (e.message || 'unknown error'), 'error');
    }
    setUploading(false);
  };

  // ── Submit evidence (move to EVIDENCE_SUBMITTED) ──
  const handleSubmitEvidence = async (taskId) => {
    setSubmitting(true);
    try {
      const res = await api.ccSubmitAttestation(taskId, { notes: submitForm.notes, evidenceType: 'DOCUMENT' });
      if (res.ok) {
        addToast('Evidence submitted for review', 'success');
        setExpandedId(null);
        setSubmitForm({ notes: '' });
        loadData();
      }
    } catch (e) { addToast('Error submitting attestation', 'error'); }
    setSubmitting(false);
  };

  // ── Review (approve/reject) ──
  const handleReview = async (taskId, approved) => {
    setReviewing(true);
    try {
      const res = await api.ccReviewAttestation(taskId, {
        approved,
        notes: reviewForm.notes,
        feedback: !approved ? reviewForm.rejectionReason : undefined,
      });
      if (res.ok) {
        addToast(approved ? 'Attestation approved' : 'Attestation rejected — submitter notified', approved ? 'success' : 'warning');
        setExpandedId(null);
        setReviewForm({ notes: '', rejectionReason: '' });
        loadData();
      }
    } catch (e) { addToast('Review failed', 'error'); }
    setReviewing(false);
  };

  // ── Delete evidence ──
  const handleDeleteEvidence = async (taskId, evidenceId) => {
    try {
      const res = await api.ccDeleteEvidence(taskId, evidenceId);
      if (res.ok) {
        // Optimistically remove just the deleted item from local state
        setEvidenceItems(prev => ({
          ...prev,
          [taskId]: (prev[taskId] || []).filter(e => (e.evidenceId || e.id) !== evidenceId)
        }));
        loadEvidence(taskId, true); // force reload to sync with backend
        addToast('Evidence removed', 'success');
      }
    } catch (e) { addToast('Failed to remove evidence', 'error'); }
  };

  if (loading) return <LoadingSpinner />;

  const isTerminal = (s) => ['APPROVED', 'REJECTED', 'CANCELLED', 'EXPIRED'].includes(s);
  const isReviewable = (s) => ['EVIDENCE_SUBMITTED', 'UNDER_REVIEW'].includes(s);
  const needsEvidence = (s) => ['CREATED', 'ASSIGNED', 'REJECTED'].includes(s);

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      <SubViewToggle
        views={[
          { key: 'MY_QUEUE', label: 'My Queue', icon: Inbox },
          { key: 'REVIEW_QUEUE', label: 'Review Queue', icon: ShieldCheck },
          { key: 'ASSIGNED_BY_ME', label: 'Assigned by Me', icon: Send },
          { key: 'HISTORY', label: 'History', icon: Clock },
          // Phase 5 — read-only timeline of unified-pipeline activations.
          // Spec: docs/UNIFIED_POLICY_PIPELINE_SPEC.md.
          { key: 'AUTO_EVIDENCED', label: 'Auto-Evidenced', icon: Sparkles },
        ]}
        active={subView}
        onChange={setSubView}
      />

      {/* ── Phase 5: Auto-Evidenced Controls (activations timeline) ── */}
      {subView === 'AUTO_EVIDENCED' ? (
        <AutoEvidencedSection
          activations={activations}
          loading={activationsLoading}
          onRefresh={loadActivations}
        />
      ) : attestations.length === 0 ? (
        <EmptyState icon={FileCheck} title="No attestations" subtitle={`No attestations in ${subView.replace(/_/g, ' ').toLowerCase()}`} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {attestations.map(att => {
            const isExpanded = expandedId === att.id;
            const isOverdue = att.dueDate && new Date(att.dueDate) < new Date() && !isTerminal(att.status);
            const evidence = evidenceItems[att.id] || [];
            const evidencePayload = att.evidencePayload
              ? (Array.isArray(att.evidencePayload) ? att.evidencePayload : [])
              : [];
            const allEvidence = evidence.length > 0 ? evidence : evidencePayload;
            const wasRejected = att.status === 'REJECTED';

            // Determine accent color
            const cardAccent = wasRejected ? ACCENT.rose.icon
              : att.status === 'APPROVED' ? ACCENT.emerald.icon
              : isOverdue ? ACCENT.rose.icon
              : att.status === 'EVIDENCE_SUBMITTED' || att.status === 'UNDER_REVIEW' ? ACCENT.amber.icon
              : ACCENT.sky.icon;

            return (
              <CCCard key={att.id} accent={cardAccent} style={{ padding: 0 }}>
                {/* ── Summary Header ── */}
                <div style={{ padding: '16px 20px', cursor: 'pointer' }} onClick={() => handleExpand(att)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Control + Framework */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>
                          {att.controlCode ? `${att.controlCode} — ` : ''}{att.controlName || 'Unnamed Control'}
                        </span>
                      </div>

                      {/* Agent Name */}
                      {att.agentName && (
                        <div style={{ fontSize: 12, color: S[500], marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Bot size={12} /> Agent: <span style={{ fontWeight: 600, color: S[700] }}>{att.agentName}</span>
                        </div>
                      )}

                      {/* Badges Row */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <StatusBadge status={att.status} />
                        {att.escalationLevel > 0 && (
                          <Badge text={`Escalation L${att.escalationLevel}`} color={ACCENT.rose.text} bg={ACCENT.rose.bg} />
                        )}
                        <Badge
                          text={`Due: ${att.dueDate ? new Date(att.dueDate).toLocaleDateString() : '—'}`}
                          color={isOverdue ? ACCENT.rose.text : S[600]}
                          bg={isOverdue ? ACCENT.rose.bg : S[100]}
                        />
                        {att.assigneeName && (
                          <Badge text={`Assignee: ${att.assigneeName}`} color={ACCENT.sky.text} bg={ACCENT.sky.bg} />
                        )}
                        {att.reviewerName && (
                          <Badge text={`Reviewer: ${att.reviewerName}`} color={ACCENT.violet.text} bg={ACCENT.violet.bg} />
                        )}
                        <AIBadge att={att} />
                        {allEvidence.length > 0 && (
                          <Badge text={`${allEvidence.length} evidence`} color={ACCENT.emerald.text} bg={ACCENT.emerald.bg} />
                        )}
                      </div>
                    </div>
                    <div style={{ padding: 6, borderRadius: 6, color: S[400], flexShrink: 0 }}>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>
                </div>

                {/* ── Expanded Detail Panel ── */}
                {isExpanded && (
                  <div style={{
                    borderTop: `1px solid ${S[200]}`, padding: '20px',
                    background: S[25], animation: 'ccSlideUp 0.2s ease',
                  }}>
                    {/* ── Metadata Grid ── */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px',
                      padding: '12px 16px', background: '#fff', borderRadius: 8,
                      border: `1px solid ${S[200]}`, marginBottom: 16,
                    }}>
                      <AttDetailRow label="Control" value={att.controlCode ? `${att.controlCode} — ${att.controlName}` : att.controlName} />
                      <AttDetailRow label="Agent" value={att.agentName} />
                      {/* Phase 9 — Re-assignment affordance.
                          Only render the picker for tasks that are still
                          actionable (not approved/cancelled/expired) and
                          when the current user has reassign authority. The
                          picker call goes through the existing PUT
                          /attestations/{id} endpoint (ccUpdateAttestation). */}
                      <ReassignmentRow
                        att={att}
                        isReassignable={!isTerminal(att.status)}
                        onReassigned={() => loadData()}
                        addToast={addToast}
                      />
                      <AttDetailRow label="Assigned By" value={att.assignedByName || '—'} />
                      <AttDetailRow label="Reviewer" value={att.reviewerName || 'Not assigned'} />
                      <AttDetailRow label="Due Date" value={att.dueDate ? new Date(att.dueDate).toLocaleDateString() : '—'} accent={isOverdue ? ACCENT.rose.text : undefined} />
                      {att.attestationPeriodStart && (
                        <AttDetailRow label="Coverage Period"
                          value={`${new Date(att.attestationPeriodStart).toLocaleDateString()} — ${att.attestationPeriodEnd ? new Date(att.attestationPeriodEnd).toLocaleDateString() : 'Ongoing'}`}
                        />
                      )}
                      {att.nextAttestationDue && (
                        <AttDetailRow label="Next Attestation" value={new Date(att.nextAttestationDue).toLocaleDateString()} />
                      )}
                      <AttDetailRow label="Task ID" value={att.id?.substring(0, 8)} mono />
                      {att.escalationLevel > 0 && (
                        <AttDetailRow label="Escalation" value={`Level ${att.escalationLevel}`} accent={ACCENT.rose.text} />
                      )}
                    </div>

                    {/* ── AI Analysis Section ── */}
                    {(att.aiPreFilled || att.aiEvidenceSummary || att.aiRecommendation) && (
                      <div style={{
                        padding: '12px 16px', background: ACCENT.violet.bg, borderRadius: 8,
                        border: `1px solid ${ACCENT.violet.border}`, marginBottom: 16,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT.violet.text, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Sparkles size={13} /> ComplianceAssist Analysis
                        </div>
                        {att.aiRecommendation && (
                          <div style={{ fontSize: 12, color: ACCENT.violet.text, marginBottom: 4 }}>
                            Recommendation: <strong>{att.aiRecommendation}</strong>
                            {att.aiConfidenceScore != null && <span> (confidence: {(att.aiConfidenceScore * 100).toFixed(0)}%)</span>}
                          </div>
                        )}
                        {att.aiEvidenceSummary && (
                          <div style={{ fontSize: 12, color: S[700], lineHeight: 1.5, padding: '8px 12px', background: '#fff', borderRadius: 6, marginTop: 6 }}>
                            {att.aiEvidenceSummary}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Evidence Section ── */}
                    <div style={{
                      padding: '12px 16px', background: '#fff', borderRadius: 8,
                      border: `1px solid ${S[200]}`, marginBottom: 16,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={13} color={ACCENT.sky.icon} />
                        Evidence ({allEvidence.length} items)
                      </div>

                      {loadingEvidence[att.id] && (
                        <div style={{ padding: 12, textAlign: 'center', color: S[400], fontSize: 12 }}>
                          <Loader size={14} style={{ animation: 'ccSpin 1s linear infinite' }} /> Loading evidence...
                        </div>
                      )}

                      {allEvidence.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {allEvidence.map((item, i) => (
                            <EvidenceItem
                              key={item.evidenceId || item.id || i}
                              item={item}
                              index={i}
                              taskId={att.id}
                              readOnly={isTerminal(att.status)}
                              onDelete={!isTerminal(att.status) ? (eid) => handleDeleteEvidence(att.id, eid) : null}
                            />
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: S[400], fontStyle: 'italic', padding: '8px 0' }}>
                          No evidence attached yet.
                        </div>
                      )}

                      {/* Upload area for active tasks */}
                      {needsEvidence(att.status) && (
                        <div style={{ marginTop: 10 }}>
                          <label style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            padding: '10px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            border: `2px dashed ${S[300]}`, color: S[500], cursor: 'pointer',
                            background: S[50], transition: 'all 0.15s',
                          }}>
                            <Plus size={14} /> {uploading ? 'Uploading...' : 'Upload Evidence File'}
                            <input type="file" style={{ display: 'none' }} disabled={uploading}
                              onChange={(e) => e.target.files?.[0] && handleUploadEvidence(att.id, e.target.files[0])} />
                          </label>
                        </div>
                      )}
                    </div>

                    {/* ── Submission History / Evidence Submitted By ── */}
                    {att.evidenceSubmittedBy && (
                      <div style={{
                        padding: '10px 16px', background: ACCENT.sky.bg, borderRadius: 8,
                        border: `1px solid ${ACCENT.sky.border}`, marginBottom: 16,
                        fontSize: 12, color: ACCENT.sky.text, display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <Send size={13} />
                        Evidence submitted by <strong>{att.submitterName || att.evidenceSubmittedBy?.substring(0, 8)}</strong>
                        {att.evidenceSubmittedAt && <span> on {new Date(att.evidenceSubmittedAt).toLocaleString()}</span>}
                      </div>
                    )}

                    {/* ── Review Outcome (for terminal/rejected states) ── */}
                    {(att.reviewedAt || att.reviewNotes || att.rejectionReason) && (
                      <div style={{
                        padding: '12px 16px', borderRadius: 8, marginBottom: 16,
                        background: att.status === 'APPROVED' ? ACCENT.emerald.bg : att.status === 'REJECTED' ? ACCENT.rose.bg : S[50],
                        border: `1px solid ${att.status === 'APPROVED' ? ACCENT.emerald.border : att.status === 'REJECTED' ? ACCENT.rose.border : S[200]}`,
                      }}>
                        <div style={{
                          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
                          color: att.status === 'APPROVED' ? ACCENT.emerald.text : att.status === 'REJECTED' ? ACCENT.rose.text : S[600],
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          {att.status === 'APPROVED' ? <CheckCircle size={13} /> : att.status === 'REJECTED' ? <XCircle size={13} /> : <Shield size={13} />}
                          Review Outcome
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: S[600] }}>Decision:</span>
                            <StatusBadge status={att.status} />
                          </div>
                          {att.reviewerName && (
                            <div style={{ fontSize: 12, color: S[700] }}>
                              Reviewed by: <strong>{att.reviewerName}</strong>
                              {att.reviewedAt && <span style={{ color: S[500] }}> on {new Date(att.reviewedAt).toLocaleString()}</span>}
                            </div>
                          )}
                          {att.rejectionReason && (
                            <div style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: ACCENT.rose.text }}>Rejection Reason:</span>
                              <div style={{ fontSize: 12, color: S[700], marginTop: 4, padding: '8px 12px', background: '#fff', borderRadius: 6, border: `1px solid ${ACCENT.rose.border}` }}>
                                {att.rejectionReason}
                              </div>
                            </div>
                          )}
                          {att.reviewNotes && (
                            <div style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: S[600] }}>Reviewer Notes:</span>
                              <div style={{ fontSize: 12, color: S[700], marginTop: 4, padding: '8px 12px', background: '#fff', borderRadius: 6, border: `1px solid ${S[200]}` }}>
                                {att.reviewNotes}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Action Area ── */}

                    {/* Submit evidence for review (assignee view) */}
                    {needsEvidence(att.status) && (
                      <div style={{
                        padding: '12px 16px', background: '#fff', borderRadius: 8,
                        border: `1px solid ${S[200]}`,
                      }}>
                        {att.status === 'REJECTED' && (
                          <div style={{
                            padding: '8px 12px', background: ACCENT.amber.bg, borderRadius: 6,
                            border: `1px solid ${ACCENT.amber.border}`, marginBottom: 12,
                            fontSize: 12, color: ACCENT.amber.text, display: 'flex', alignItems: 'center', gap: 6,
                          }}>
                            <AlertTriangle size={13} />
                            This attestation was rejected. Please address the reviewer's feedback and re-submit with updated evidence.
                          </div>
                        )}
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                          Submission Notes
                        </label>
                        <textarea
                          value={submitForm.notes}
                          onChange={(e) => setSubmitForm({ ...submitForm, notes: e.target.value })}
                          placeholder="Describe the evidence provided and how it satisfies this control requirement..."
                          style={{ ...inputBase, minHeight: 70, resize: 'vertical', marginBottom: 12 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="cc-btn-primary" disabled={submitting} onClick={() => handleSubmitEvidence(att.id)} style={{
                            ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
                            background: BRAND.gradient, color: '#fff', boxShadow: '0 1px 4px rgba(226,74,58,0.2)',
                            opacity: submitting ? 0.6 : 1,
                          }}>
                            {submitting ? <Loader size={13} style={{ animation: 'ccSpin 1s linear infinite' }} /> : <Send size={13} />}
                            {att.status === 'REJECTED' ? 'Re-submit Evidence' : 'Submit for Review'}
                          </button>
                          <button className="cc-btn-ghost" onClick={() => setExpandedId(null)} style={{
                            ...btnBase, padding: '8px 14px', fontSize: 12, fontWeight: 500,
                            background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                          }}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Reviewer action area */}
                    {isReviewable(att.status) && subView === 'REVIEW_QUEUE' && (
                      <div style={{
                        padding: '12px 16px', background: '#fff', borderRadius: 8,
                        border: `1px solid ${ACCENT.violet.border}`,
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT.violet.text, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ShieldCheck size={13} /> Review This Attestation
                        </div>
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4 }}>Review Notes</label>
                        <textarea
                          value={reviewForm.notes}
                          onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                          placeholder="Assessment notes for the submitter..."
                          style={{ ...inputBase, minHeight: 60, resize: 'vertical', marginBottom: 10 }}
                        />
                        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4 }}>Rejection Reason (required if rejecting)</label>
                        <textarea
                          value={reviewForm.rejectionReason}
                          onChange={(e) => setReviewForm({ ...reviewForm, rejectionReason: e.target.value })}
                          placeholder="If rejecting, explain what's missing or needs correction..."
                          style={{ ...inputBase, minHeight: 50, resize: 'vertical', marginBottom: 12 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="cc-btn-primary" disabled={reviewing} onClick={() => handleReview(att.id, true)} style={{
                            ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
                            background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                            opacity: reviewing ? 0.6 : 1,
                          }}>
                            <CheckCircle size={13} /> Approve
                          </button>
                          <button disabled={reviewing || !reviewForm.rejectionReason.trim()} onClick={() => handleReview(att.id, false)} style={{
                            ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
                            background: ACCENT.rose.bg, color: ACCENT.rose.text,
                            border: `1px solid ${ACCENT.rose.border}`,
                            opacity: (reviewing || !reviewForm.rejectionReason.trim()) ? 0.5 : 1,
                            cursor: (!reviewForm.rejectionReason.trim()) ? 'not-allowed' : 'pointer',
                          }}>
                            <XCircle size={13} /> Reject & Request Re-submission
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CCCard>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   PHASE 5 — Auto-Evidenced Controls section
   ─────────────────────────────────────────────────────────────────────────────
   Read-only timeline of unified-pipeline activations. One row per
   PolicyAssignmentActivation (immutable audit snapshot from Stage 5 of the
   wizard). Counts show how many rules / bindings / attestation tasks were
   created in that single transactional commit. Expanding a row reveals the
   embedded JSONB arrays so operators can trace provenance back to specific
   rule and task UUIDs without leaving the Compliance Center.
   ══════════════════════════════════════════════════════════════════════════════ */
function AutoEvidencedSection({ activations, loading, onRefresh }) {
  const [expandedId, setExpandedId] = useState(null);

  if (loading) {
    return <LoadingSpinner />;
  }
  if (!activations || activations.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No auto-evidenced controls yet"
        subtitle="Activations from the unified policy assignment wizard appear here as they happen."
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '2px 4px', marginBottom: 4,
      }}>
        <div style={{ fontSize: 12, color: S[600] }}>
          {activations.length} recent activation{activations.length === 1 ? '' : 's'}
          {' '}— each is an immutable audit snapshot of a single Stage 5 commit.
        </div>
        <button onClick={onRefresh} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 12, padding: '4px 10px', borderRadius: 6,
          border: `1px solid ${S[200]}`, background: '#fff', color: S[600], cursor: 'pointer',
        }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {activations.map(act => {
        const isExpanded = expandedId === act.id;
        const ts = act.createdAt ? new Date(act.createdAt) : null;
        const ruleIds = Array.isArray(act.ruleIds) ? act.ruleIds : (act.ruleIds?._items || []);
        const bindingIds = Array.isArray(act.bindingIds) ? act.bindingIds : (act.bindingIds?._items || []);
        const taskIds = Array.isArray(act.attestationTaskIds) ? act.attestationTaskIds : (act.attestationTaskIds?._items || []);
        const sources = Array.isArray(act.sources) ? act.sources : (act.sources?._items || []);
        return (
          <CCCard key={act.id} accent={ACCENT.sky}>
            <div
              onClick={() => setExpandedId(isExpanded ? null : act.id)}
              style={{ cursor: 'pointer', padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}
            >
              <div style={{
                background: ACCENT.sky.bg, padding: 8, borderRadius: 8,
                border: `1px solid ${ACCENT.sky.border}`,
              }}>
                <Sparkles size={18} style={{ color: ACCENT.sky.icon }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: S[900] }}>
                  Policy Activation • {ts ? ts.toLocaleString() : '—'}
                </div>
                <div style={{ fontSize: 11, color: S[600], marginTop: 2 }}>
                  {act.frameworkBindingId
                    ? <>Framework binding <code style={{ fontFamily: 'ui-monospace, monospace' }}>{String(act.frameworkBindingId).slice(0, 8)}…</code></>
                    : 'Direct binding (no framework provenance)'}
                  {act.triggeredByRole ? <> • {act.triggeredByRole}</> : null}
                  {act.pipelineVersion ? <> • pipeline {act.pipelineVersion}</> : null}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 14 }}>
                <div style={{ textAlign: 'center', minWidth: 36 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>{act.ruleCount ?? ruleIds.length}</div>
                  <div style={{ fontSize: 10, color: S[500] }}>rules</div>
                </div>
                <div style={{ textAlign: 'center', minWidth: 50 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>{act.bindingCount ?? bindingIds.length}</div>
                  <div style={{ fontSize: 10, color: S[500] }}>bindings</div>
                </div>
                <div style={{ textAlign: 'center', minWidth: 70 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>{act.attestationCount ?? taskIds.length}</div>
                  <div style={{ fontSize: 10, color: S[500] }}>attestations</div>
                </div>
              </div>
              {isExpanded
                ? <ChevronUp size={14} style={{ color: S[400] }} />
                : <ChevronDown size={14} style={{ color: S[400] }} />}
            </div>

            {isExpanded && (
              <div style={{ borderTop: `1px solid ${S[200]}`, padding: '14px 14px 16px' }}>
                {act.reason && (
                  <div style={{
                    fontSize: 12, color: S[700], padding: 10, borderRadius: 8,
                    background: S[50], marginBottom: 12, fontStyle: 'italic',
                  }}>
                    "{act.reason}"
                  </div>
                )}
                {sources.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: S[600],
                      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
                    }}>Sources</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {sources.map((s, i) => (
                        <span key={i} style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 6,
                          background: ACCENT.violet.bg, color: ACCENT.violet.text,
                          border: `1px solid ${ACCENT.violet.border}`,
                        }}>
                          {s.type || 'SOURCE'}{s.label ? ` — ${s.label}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <ActivationIdList label="Rules" ids={ruleIds} />
                  <ActivationIdList label="Bindings" ids={bindingIds} />
                  <ActivationIdList label="Attestation Tasks" ids={taskIds} />
                </div>
              </div>
            )}
          </CCCard>
        );
      })}
    </div>
  );
}

function ActivationIdList({ label, ids }) {
  if (!ids || ids.length === 0) {
    return (
      <div>
        <div style={{
          fontSize: 11, fontWeight: 700, color: S[600],
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
        }}>{label}</div>
        <div style={{ fontSize: 11, color: S[400], fontStyle: 'italic' }}>none</div>
      </div>
    );
  }
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: S[600],
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
      }}>{label} ({ids.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 120, overflowY: 'auto' }}>
        {ids.map((id, i) => (
          <code key={i} style={{
            fontSize: 10, color: S[600], padding: '2px 6px',
            background: S[50], borderRadius: 4,
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }} title={String(id)}>
            {String(id).slice(0, 13)}…
          </code>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   TAB 3: REMEDIATION — Full Workflow
   ══════════════════════════════════════════════════════════════════════════════ */

/* ── Remediation detail row ── */
const RemDetailRow = ({ label, value, mono, accent }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12 }}>
      <span style={{ fontWeight: 600, color: S[500], minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{
        color: accent || S[800],
        fontFamily: mono ? "'Consolas', monospace" : 'inherit',
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );
};

/* ── SLA progress bar ── */
const SLABar = ({ task }) => {
  if (!task.slaDeadline) return null;
  const now = Date.now();
  const created = new Date(task.createdAt).getTime();
  const deadline = new Date(task.slaDeadline).getTime();
  const total = deadline - created;
  const elapsed = now - created;
  const pct = total > 0 ? Math.min(100, Math.max(0, (elapsed / total) * 100)) : 0;
  const breached = now > deadline && !['RESOLVED', 'CLOSED', 'WONT_FIX'].includes(task.status);
  const remaining = deadline - now;
  const hoursLeft = Math.max(0, Math.floor(remaining / 3600000));
  const daysLeft = Math.floor(hoursLeft / 24);

  const barColor = breached ? '#ef4444' : pct > 80 ? '#f59e0b' : '#10b981';
  const label = breached ? 'SLA BREACHED' : daysLeft > 0 ? `${daysLeft}d ${hoursLeft % 24}h remaining` : `${hoursLeft}h remaining`;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: breached ? '#ef4444' : S[500], textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: S[400] }}>{Math.round(pct)}%</span>
      </div>
      <div style={{ width: '100%', height: 4, borderRadius: 2, background: S[150] }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: barColor, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
};

/* ── Trigger type icon/label ── */
const TRIGGER_META = {
  CONTROL_FAILURE: { icon: Shield, label: 'Policy Violation', accent: ACCENT.rose },
  ATTESTATION_REJECTED: { icon: XCircle, label: 'Attestation Rejected', accent: ACCENT.amber },
  DRIFT_ALERT: { icon: AlertTriangle, label: 'Drift Alert', accent: ACCENT.amber },
  AUDIT_FINDING: { icon: Search, label: 'Audit Finding', accent: ACCENT.violet },
  MANUAL: { icon: Edit2, label: 'Manually Created', accent: ACCENT.slate },
};

const ROOT_CAUSE_LABELS = {
  CONFIG: 'Configuration Error', CODE: 'Code Defect', PROCESS: 'Process Gap',
  TRAINING: 'Training Gap', VENDOR: 'Vendor Issue', UNKNOWN: 'Unknown',
};

const RESOLUTION_LABELS = {
  FIXED: 'Fixed', MITIGATED: 'Mitigated', ACCEPTED_RISK: 'Risk Accepted',
  FALSE_POSITIVE: 'False Positive', WONT_FIX: "Won't Fix",
};

function RemediationTab() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ACTIVE');
  const [expandedId, setExpandedId] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [resolveForm, setResolveForm] = useState({ resolutionType: '', notes: '', rootCause: '', rootCauseNotes: '' });
  const [acting, setActing] = useState(false);
  const [escalateModal, setEscalateModal] = useState({ open: false, taskId: null, chain: [], reason: '' });
  const [slaConfigOpen, setSlaConfigOpen] = useState(false);
  const [slaConfig, setSlaConfig] = useState(null);
  const [slaForm, setSlaForm] = useState({ criticalHours: 4, highHours: 24, mediumHours: 72, lowHours: 168 });
  const [slaSaving, setSlaSaving] = useState(false);
  const { addToast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusMap = {
        ACTIVE: 'OPEN',
        IN_PROGRESS: 'IN_PROGRESS',
        PENDING_VERIFICATION: 'PENDING_VERIFICATION',
        RESOLVED: 'RESOLVED',
        ALL: undefined,
      };
      const params = {};
      if (statusMap[filter] !== undefined) params.status = statusMap[filter];
      const res = await api.ccListRemediationTasks(params);
      if (res.ok) setTasks(Array.isArray(res.data) ? res.data : res.data?.tasks || []);
    } catch (e) { addToast('Failed to load remediation tasks', 'error'); }
    setLoading(false);
  }, [filter, addToast]);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await api.ccGetSLAMetrics();
      if (res.ok) setMetrics(res.data);
    } catch (_) { /* metrics are optional */ }
  }, []);

  const fetchSLAConfig = useCallback(async () => {
    try {
      const res = await api.ccGetSLAConfig();
      if (res.ok) {
        setSlaConfig(res.data);
        setSlaForm({
          criticalHours: res.data.criticalHours ?? 4,
          highHours: res.data.highHours ?? 24,
          mediumHours: res.data.mediumHours ?? 72,
          lowHours: res.data.lowHours ?? 168,
        });
      }
    } catch (_) { /* SLA config is optional */ }
  }, []);

  const handleSaveSLAConfig = async () => {
    setSlaSaving(true);
    try {
      const res = await api.ccSaveSLAConfig(slaForm);
      if (res.ok) {
        addToast('SLA configuration saved', 'success');
        fetchSLAConfig();
      } else addToast('Failed to save SLA config', 'error');
    } catch (_) { addToast('Error saving SLA config', 'error'); }
    setSlaSaving(false);
  };

  const handleResetSLAConfig = async () => {
    setSlaSaving(true);
    try {
      const res = await api.ccDeleteSLAConfig();
      if (res.ok) {
        addToast('SLA config reset to defaults', 'success');
        fetchSLAConfig();
      } else addToast('Failed to reset SLA config', 'error');
    } catch (_) { addToast('Error resetting SLA config', 'error'); }
    setSlaSaving(false);
  };

  useEffect(() => { fetchData(); fetchMetrics(); fetchSLAConfig(); }, [fetchData, fetchMetrics, fetchSLAConfig]);

  /* ── Actions ──
   * NOTE: After each action, we call setFilter() to switch tabs. The useEffect on
   * [fetchData, fetchMetrics] automatically re-fetches when filter changes (since
   * fetchData depends on filter via useCallback). We must NOT call fetchData() manually
   * after setFilter() — the stale closure still holds the old filter value, which would
   * fetch the wrong status and show stale tasks under the new tab.
   */
  const handleStartWork = async (taskId) => {
    setActing(true);
    try {
      const res = await api.ccUpdateRemediationTask(taskId, { status: 'IN_PROGRESS', notes: 'Work started' });
      if (res.ok) { addToast('Task moved to In Progress', 'success'); setFilter('IN_PROGRESS'); }
      else addToast('Failed to update task', 'error');
    } catch (_) { addToast('Error updating task', 'error'); }
    setActing(false);
  };

  const handleResolve = async (taskId) => {
    if (!resolveForm.resolutionType) { addToast('Please select a resolution type', 'error'); return; }
    setActing(true);
    try {
      // Bundle root cause into the resolution notes (avoids race condition with separate status update)
      let combinedNotes = resolveForm.notes || '';
      if (resolveForm.rootCause) {
        const rootCausePrefix = `Root cause: ${resolveForm.rootCause} — ${resolveForm.rootCauseNotes || 'N/A'}`;
        combinedNotes = combinedNotes ? `${rootCausePrefix}\n\n${combinedNotes}` : rootCausePrefix;
      }
      const res = await api.ccResolveRemediation(taskId, {
        resolutionType: resolveForm.resolutionType,
        notes: combinedNotes,
      });
      if (res.ok) {
        addToast('Resolution submitted — pending verification', 'success');
        setResolveForm({ resolutionType: '', notes: '', rootCause: '', rootCauseNotes: '' });
        setExpandedId(null);
        setFilter('PENDING_VERIFICATION');
      } else addToast('Failed to resolve task', 'error');
    } catch (_) { addToast('Error resolving task', 'error'); }
    setActing(false);
  };

  const handleVerify = async (taskId) => {
    setActing(true);
    try {
      const res = await api.ccVerifyRemediation(taskId);
      if (res.ok) { addToast('Verification submitted', 'success'); setExpandedId(null); setFilter('RESOLVED'); }
      else addToast('Failed to verify', 'error');
    } catch (_) { addToast('Error verifying remediation', 'error'); }
    setActing(false);
  };

  const openEscalateModal = async (taskId) => {
    setEscalateModal({ open: true, taskId, chain: [], reason: '' });
    try {
      const res = await api.ccGetEscalationChain(taskId);
      if (res.ok) {
        const chain = await res.json();
        setEscalateModal(m => ({ ...m, chain }));
      }
    } catch (_) { /* chain preview failed — non-blocking */ }
  };

  const handleEscalate = async () => {
    if (!escalateModal.taskId) return;
    setActing(true);
    try {
      const res = await api.ccEscalateRemediation(escalateModal.taskId, { reason: escalateModal.reason || 'Escalated — requires intervention' });
      if (res.ok) {
        const data = await res.json();
        addToast(`Escalated to level ${data.escalationLevel}${data.escalatedToName ? ' → ' + data.escalatedToName : ''}`, 'success');
        setEscalateModal({ open: false, taskId: null, chain: [], reason: '' });
        fetchData(); fetchMetrics();
      } else {
        const err = await res.json().catch(() => ({}));
        addToast(err.message || 'Failed to escalate', 'error');
      }
    } catch (_) { addToast('Error escalating task', 'error'); }
    setActing(false);
  };

  if (loading) return <LoadingSpinner />;

  const activeTasks = tasks.filter(t => !['RESOLVED', 'CLOSED', 'WONT_FIX'].includes(t.status));
  const breachedCount = tasks.filter(t => {
    if (!t.slaDeadline) return false;
    return Date.now() > new Date(t.slaDeadline).getTime() && !['RESOLVED', 'CLOSED', 'WONT_FIX'].includes(t.status);
  }).length;

  /* ── Manual create handler ── */
  const handleCreateManual = async () => {
    setActing(true);
    try {
      const res = await api.ccCreateRemediation({
        description: 'Manually created remediation task for testing',
      });
      if (res.ok) {
        addToast('Remediation task created', 'success');
        setFilter('ACTIVE'); // Switch to Open tab to show new task
        fetchData();
        fetchMetrics();
      } else {
        addToast(res.data?.message || 'Failed to create task', 'error');
      }
    } catch (_) { addToast('Error creating task', 'error'); }
    setActing(false);
  };

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      {/* ── SLA Metrics Strip ── */}
      {metrics && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16,
        }}>
          {[
            { label: 'Active Tasks', value: metrics.openTasks ?? activeTasks.length, accent: ACCENT.sky },
            { label: 'SLA Compliance', value: metrics.overallSLAComplianceRate != null ? `${Math.round(metrics.overallSLAComplianceRate * 100)}%` : '—', accent: ACCENT.emerald },
            { label: 'Avg Resolution', value: (() => {
              if (metrics.avgMTTR == null) return '—';
              if (typeof metrics.avgMTTR === 'number') return `${Math.round(metrics.avgMTTR / 3600)}h`;
              // Java Duration serializes as ISO-8601 string like "PT2H30M" or seconds number
              const s = String(metrics.avgMTTR);
              const m = s.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
              if (m) return `${parseInt(m[1] || '0')}h`;
              return '—';
            })(), accent: ACCENT.amber },
            { label: 'SLA Breached', value: metrics.overdueTasks ?? breachedCount, accent: ACCENT.rose },
          ].map((m, i) => (
            <div key={i} className="cc-stat-card" style={{
              padding: '14px 16px', borderRadius: 10, background: '#fff',
              border: `1px solid ${S[200]}`, textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: m.accent.icon }}>{m.value}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── SLA Configuration + Create Remediation ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={() => setSlaConfigOpen(o => !o)} style={{
          ...btnBase, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          fontSize: 11, fontWeight: 600, color: S[600], background: S[50],
          border: `1px solid ${S[200]}`, borderRadius: 8, cursor: 'pointer',
        }}>
          <Settings size={13} /> SLA Configuration
          {slaConfigOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {slaConfig?.isCustom && (
            <span style={{ fontSize: 9, fontWeight: 700, color: BRAND.primary, background: BRAND.light,
              padding: '2px 6px', borderRadius: 4, marginLeft: 4, }}>CUSTOM</span>
          )}
          {slaConfig?.isInherited && (
            <span style={{ fontSize: 9, fontWeight: 700, color: ACCENT.sky.text, background: ACCENT.sky.bg,
              padding: '2px 6px', borderRadius: 4, marginLeft: 4, }}>INHERITED</span>
          )}
        </button>
        <button disabled={acting} onClick={handleCreateManual} className="cc-btn-primary" style={{
          ...btnBase, padding: '8px 16px', fontSize: 12, fontWeight: 700,
          background: BRAND.gradient, color: '#fff', opacity: acting ? 0.6 : 1,
        }}>
          <Plus size={13} /> Create Remediation
        </button>
      </div>
        {slaConfigOpen && (
          <div style={{
            marginTop: -8, marginBottom: 16, padding: 20, borderRadius: 10, background: '#fff',
            border: `1px solid ${S[200]}`, animation: 'ccFadeIn 0.2s ease',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: S[800] }}>Priority SLA Deadlines</div>
                <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>
                  Configure how long each priority level has before SLA breach.
                  {slaConfig?.resolvedScope && (
                    <span style={{ marginLeft: 6, fontWeight: 600, color: ACCENT.sky.text }}>
                      Resolved at {slaConfig.resolvedScope} scope
                    </span>
                  )}
                </div>
              </div>
              {slaConfig?.isCustom && (
                <button disabled={slaSaving} onClick={handleResetSLAConfig} style={{
                  ...btnBase, padding: '6px 12px', fontSize: 10, fontWeight: 600,
                  color: ACCENT.rose.text, background: ACCENT.rose.bg, border: `1px solid ${ACCENT.rose.border}`,
                  opacity: slaSaving ? 0.5 : 1,
                }}>
                  Reset to Defaults
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { key: 'criticalHours', label: 'Critical', color: priorityColors.CRITICAL, defaultVal: 4 },
                { key: 'highHours', label: 'High', color: priorityColors.HIGH, defaultVal: 24 },
                { key: 'mediumHours', label: 'Medium', color: priorityColors.MEDIUM, defaultVal: 72 },
                { key: 'lowHours', label: 'Low', color: priorityColors.LOW, defaultVal: 168 },
              ].map(({ key, label, color, defaultVal }) => {
                const hours = slaForm[key];
                const displayDays = hours >= 24 ? `${(hours / 24).toFixed(hours % 24 === 0 ? 0 : 1)}d` : `${hours}h`;
                const isDefault = hours === defaultVal;
                return (
                  <div key={key} style={{
                    padding: 14, borderRadius: 8, border: `1px solid ${S[200]}`,
                    background: isDefault ? S[25] : '#fffbf0',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: S[700], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
                      {!isDefault && (
                        <span style={{ fontSize: 9, fontWeight: 600, color: ACCENT.amber.text, background: ACCENT.amber.bg,
                          padding: '1px 5px', borderRadius: 3, marginLeft: 'auto', }}>modified</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="number" min="1" max="8760" value={hours}
                        onChange={e => setSlaForm(f => ({ ...f, [key]: Math.max(1, parseInt(e.target.value) || 1) }))}
                        style={{
                          width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                          border: `1px solid ${S[200]}`, outline: 'none', color: S[800], background: '#fff',
                        }}
                      />
                      <span style={{ fontSize: 10, color: S[400], fontWeight: 600, whiteSpace: 'nowrap' }}>hrs ({displayDays})</span>
                    </div>
                    <div style={{ fontSize: 9, color: S[400], marginTop: 4 }}>
                      Default: {defaultVal}h ({defaultVal >= 24 ? `${defaultVal / 24}d` : `${defaultVal}h`})
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button disabled={slaSaving} onClick={handleSaveSLAConfig} style={{
                ...btnBase, padding: '9px 20px', fontSize: 12, fontWeight: 700,
                background: BRAND.gradient, color: '#fff', opacity: slaSaving ? 0.5 : 1,
              }}>
                {slaSaving ? <Loader size={13} className="cc-spin" /> : <Check size={13} />}
                Save SLA Configuration
              </button>
            </div>
          </div>
        )}

      <SubViewToggle
        views={[
          { key: 'ACTIVE', label: 'Open', icon: AlertCircle, count: filter === 'ACTIVE' ? tasks.length : undefined },
          { key: 'IN_PROGRESS', label: 'In Progress', icon: Activity },
          { key: 'PENDING_VERIFICATION', label: 'Pending Verification', icon: ShieldCheck },
          { key: 'RESOLVED', label: 'Resolved', icon: CheckCircle },
          { key: 'ALL', label: 'All', icon: Inbox },
        ]}
        active={filter}
        onChange={(f) => { setFilter(f); setExpandedId(null); }}
      />

      {tasks.length === 0 ? (
        <EmptyState icon={Wrench} title="No remediation tasks" subtitle={`No ${filter.toLowerCase().replace(/_/g, ' ')} remediation tasks found`} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {tasks.map(task => {
            const pAccent = task.priority === 'CRITICAL' ? ACCENT.rose : task.priority === 'HIGH' ? ACCENT.amber : task.priority === 'MEDIUM' ? ACCENT.sky : ACCENT.slate;
            const tMeta = TRIGGER_META[task.triggerType] || TRIGGER_META.MANUAL;
            const TriggerIcon = tMeta.icon;
            const isExpanded = expandedId === task.id;
            const isOpen = ['OPEN', 'IN_PROGRESS', 'BLOCKED'].includes(task.status);
            const isPendingVerification = task.status === 'PENDING_VERIFICATION';
            const isResolved = ['RESOLVED', 'CLOSED', 'WONT_FIX'].includes(task.status);
            const breached = task.slaDeadline && Date.now() > new Date(task.slaDeadline).getTime() && !isResolved;

            return (
              <CCCard key={task.id} accent={breached ? '#ef4444' : pAccent.icon} style={{ padding: 0, overflow: 'hidden' }}>
                {/* ── Summary Row ── */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : task.id)}
                  style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 14 }}
                >
                  {/* Trigger icon */}
                  <div style={{
                    width: 34, height: 34, borderRadius: 8, background: tMeta.accent.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    border: `1px solid ${tMeta.accent.border}`, marginTop: 2,
                  }}>
                    <TriggerIcon size={15} color={tMeta.accent.icon} />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: S[900] }}>{task.title}</span>
                      {breached && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#ef4444', background: '#fef2f2', padding: '2px 6px', borderRadius: 4, border: '1px solid #fecaca' }}>
                          SLA BREACHED
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: S[500], lineHeight: 1.5, marginBottom: 8 }}>
                      {task.description ? (task.description.length > 120 ? task.description.slice(0, 120) + '…' : task.description) : task.triggerDescription || tMeta.label}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <StatusBadge status={task.status} />
                      <PriorityBadge priority={task.priority} />
                      <Badge text={tMeta.label} color={tMeta.accent.text} bg={tMeta.accent.bg} />
                      {task.controlCode && <Badge text={task.controlCode} color={ACCENT.violet.text} bg={ACCENT.violet.bg} />}
                      {task.agentName && <Badge text={task.agentName} color={ACCENT.sky.text} bg={ACCENT.sky.bg} />}
                      {task.assigneeName && <Badge text={`→ ${task.assigneeName}`} />}
                    </div>
                    <SLABar task={task} />
                  </div>

                  <div style={{ flexShrink: 0, color: S[400], marginTop: 4 }}>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>

                {/* ── Expanded Detail Panel ── */}
                {isExpanded && (
                  <div style={{
                    borderTop: `1px solid ${S[150]}`, padding: '16px 20px', background: S[25],
                    animation: 'ccSlideUp 0.2s ease',
                  }}>
                    {/* ── Metadata Grid ── */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', marginBottom: 16 }}>
                      <RemDetailRow label="Task ID" value={task.id?.slice(0, 8) + '…'} mono />
                      <RemDetailRow label="Trigger Type" value={task.triggerDescription || tMeta.label} />
                      <RemDetailRow label="Control" value={task.controlName ? `${task.controlCode || ''} — ${task.controlName}` : (task.controlCode || '—')} />
                      <RemDetailRow label="Agent" value={task.agentName || '—'} />
                      <RemDetailRow label="Framework" value={task.frameworkName || '—'} />
                      <RemDetailRow label="Assigned To" value={task.assigneeName || '—'} />
                      <RemDetailRow label="Priority" value={task.priority} accent={priorityColors[task.priority]} />
                      <RemDetailRow label="Created" value={task.createdAt ? new Date(task.createdAt).toLocaleString() : '—'} />
                      <RemDetailRow label="SLA Deadline" value={task.slaDeadline ? new Date(task.slaDeadline).toLocaleString() : 'None'} accent={breached ? '#ef4444' : undefined} />
                      {task.assignedAt && <RemDetailRow label="Assigned At" value={new Date(task.assignedAt).toLocaleString()} />}
                    </div>

                    {/* Workflow DAG link removed — system workflow debugging belongs
                       in Workflow Canvas (sidebar), not in the end-user remediation view */}

                    {/* ── Description ── */}
                    {task.description && (
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Description</div>
                        <div style={{ fontSize: 12, color: S[700], lineHeight: 1.6, background: '#fff', padding: 12, borderRadius: 8, border: `1px solid ${S[200]}` }}>
                          {task.description}
                        </div>
                      </div>
                    )}

                    {/* ── Escalation Info ── */}
                    {task.status === 'BLOCKED' && (
                      <div style={{
                        marginBottom: 16, padding: 12, borderRadius: 8,
                        background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: ACCENT.amber.text }}>
                            <AlertTriangle size={13} /> Blocked / Escalated
                          </div>
                          {task.escalationLevel > 0 && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                              background: ACCENT.amber.text, color: '#fff',
                            }}>Level {task.escalationLevel}</span>
                          )}
                        </div>
                        {task.escalationReason && <div style={{ fontSize: 12, color: ACCENT.amber.text }}>{task.escalationReason}</div>}
                        {task.escalatedToName && <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>Escalated to: <strong>{task.escalatedToName}</strong></div>}
                        {task.escalatedAt && <div style={{ fontSize: 11, color: S[400], marginTop: 2 }}>at {new Date(task.escalatedAt).toLocaleString()}</div>}
                        <button disabled={acting} onClick={() => openEscalateModal(task.id)} style={{
                          ...btnBase, padding: '6px 14px', fontSize: 11, fontWeight: 600, marginTop: 8,
                          background: 'transparent', color: ACCENT.amber.text,
                          border: `1px solid ${ACCENT.amber.border}`, opacity: acting ? 0.6 : 1,
                        }}>
                          <ArrowUpRight size={11} /> Escalate Further
                        </button>
                      </div>
                    )}

                    {/* ── Resolution Info (for resolved tasks) ── */}
                    {isResolved && (
                      <div style={{
                        marginBottom: 16, padding: 12, borderRadius: 8,
                        background: ACCENT.emerald.bg, border: `1px solid ${ACCENT.emerald.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: ACCENT.emerald.text, marginBottom: 6 }}>
                          <CheckCircle size={13} /> Resolved
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 24px' }}>
                          <RemDetailRow label="Resolution" value={RESOLUTION_LABELS[task.resolutionType] || task.resolutionType || '—'} />
                          <RemDetailRow label="Resolved By" value={task.resolvedByName || '—'} />
                          <RemDetailRow label="Resolved At" value={task.resolvedAt ? new Date(task.resolvedAt).toLocaleString() : '—'} />
                          {task.rootCause && <RemDetailRow label="Root Cause" value={ROOT_CAUSE_LABELS[task.rootCause] || task.rootCause} />}
                        </div>
                        {task.resolutionNotes && (
                          <div style={{ fontSize: 12, color: S[700], marginTop: 8, padding: 8, background: '#fff', borderRadius: 6, border: `1px solid ${S[200]}` }}>
                            {task.resolutionNotes}
                          </div>
                        )}
                        {task.rootCauseNotes && (
                          <div style={{ fontSize: 11, color: S[500], marginTop: 4 }}>Root cause notes: {task.rootCauseNotes}</div>
                        )}
                      </div>
                    )}

                    {/* ── Verification Info ── */}
                    {(task.verificationStatus && task.verificationStatus !== 'PENDING') && (
                      <div style={{
                        marginBottom: 16, padding: 12, borderRadius: 8,
                        background: task.verificationStatus === 'PASSED' ? ACCENT.emerald.bg : ACCENT.rose.bg,
                        border: `1px solid ${task.verificationStatus === 'PASSED' ? ACCENT.emerald.border : ACCENT.rose.border}`,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: task.verificationStatus === 'PASSED' ? ACCENT.emerald.text : ACCENT.rose.text }}>
                          {task.verificationStatus === 'PASSED' ? <ShieldCheck size={13} /> : <XCircle size={13} />}
                          Verification: {task.verificationStatus}
                        </div>
                        {task.verifiedByName && <div style={{ fontSize: 11, color: S[500], marginTop: 2 }}>by {task.verifiedByName}</div>}
                        {task.verifiedAt && <div style={{ fontSize: 11, color: S[400], marginTop: 1 }}>at {new Date(task.verifiedAt).toLocaleString()}</div>}
                      </div>
                    )}

                    {/* ── Action Area: Start Work (OPEN tasks) ── */}
                    {task.status === 'OPEN' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button disabled={acting} onClick={() => handleStartWork(task.id)} style={{
                          ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
                          background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#fff',
                          opacity: acting ? 0.6 : 1,
                        }}>
                          <Activity size={13} /> Start Work
                        </button>
                        <button disabled={acting} onClick={() => openEscalateModal(task.id)} style={{
                          ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 600,
                          background: 'transparent', color: ACCENT.amber.text,
                          border: `1px solid ${ACCENT.amber.border}`, opacity: acting ? 0.6 : 1,
                        }}>
                          <AlertTriangle size={13} /> Escalate
                        </button>
                      </div>
                    )}

                    {/* ── Action Area: Resolve (IN_PROGRESS tasks) ── */}
                    {task.status === 'IN_PROGRESS' && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                          Resolve This Task
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 3, display: 'block' }}>Resolution Type *</label>
                            <select value={resolveForm.resolutionType} onChange={e => setResolveForm(f => ({ ...f, resolutionType: e.target.value }))}
                              style={{ ...inputBase, padding: '8px 10px', fontSize: 12 }}>
                              <option value="">Select…</option>
                              {Object.entries(RESOLUTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 3, display: 'block' }}>Root Cause</label>
                            <select value={resolveForm.rootCause} onChange={e => setResolveForm(f => ({ ...f, rootCause: e.target.value }))}
                              style={{ ...inputBase, padding: '8px 10px', fontSize: 12 }}>
                              <option value="">Select…</option>
                              {Object.entries(ROOT_CAUSE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                        </div>
                        <textarea
                          rows={2}
                          placeholder="Resolution notes — what was done to fix/mitigate…"
                          value={resolveForm.notes}
                          onChange={e => setResolveForm(f => ({ ...f, notes: e.target.value }))}
                          style={{ ...inputBase, resize: 'vertical', fontSize: 12, marginBottom: 8 }}
                        />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button disabled={acting || !resolveForm.resolutionType} onClick={() => handleResolve(task.id)} style={{
                            ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
                            background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                            opacity: (acting || !resolveForm.resolutionType) ? 0.5 : 1,
                            cursor: !resolveForm.resolutionType ? 'not-allowed' : 'pointer',
                          }}>
                            <CheckCircle size={13} /> Submit Resolution
                          </button>
                          <button disabled={acting} onClick={() => openEscalateModal(task.id)} style={{
                            ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 600,
                            background: 'transparent', color: ACCENT.amber.text,
                            border: `1px solid ${ACCENT.amber.border}`, opacity: acting ? 0.6 : 1,
                          }}>
                            <AlertTriangle size={13} /> Block / Escalate
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Action Area: Verify (PENDING_VERIFICATION tasks) ── */}
                    {isPendingVerification && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{
                          padding: 12, borderRadius: 8, background: ACCENT.sky.bg,
                          border: `1px solid ${ACCENT.sky.border}`, marginBottom: 10,
                        }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: ACCENT.sky.text, marginBottom: 4 }}>
                            Awaiting Verification
                          </div>
                          <div style={{ fontSize: 11, color: S[600] }}>
                            Resolution: {RESOLUTION_LABELS[task.resolutionType] || task.resolutionType}
                            {task.resolvedByName && ` by ${task.resolvedByName}`}
                            {task.resolvedAt && ` on ${new Date(task.resolvedAt).toLocaleDateString()}`}
                          </div>
                          {task.resolutionNotes && <div style={{ fontSize: 11, color: S[500], marginTop: 4 }}>{task.resolutionNotes}</div>}
                        </div>
                        <button disabled={acting} onClick={() => handleVerify(task.id)} style={{
                          ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
                          background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
                          opacity: acting ? 0.6 : 1,
                        }}>
                          <ShieldCheck size={13} /> Verify Remediation
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </CCCard>
            );
          })}
        </div>
      )}

      {/* ═══ Escalation Modal ═══ */}
      {escalateModal.open && (
        <Modal onClose={() => setEscalateModal({ open: false, taskId: null, chain: [], reason: '' })} title="Escalate Task">
          <div style={{ minWidth: 400 }}>
            {escalateModal.chain.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                  Escalation Chain
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {escalateModal.chain.map((tier, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      borderRadius: 8, fontSize: 12,
                      background: tier.isCurrent ? ACCENT.amber.bg : S[50],
                      border: `1px solid ${tier.isCurrent ? ACCENT.amber.border : S[200]}`,
                    }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: tier.isCurrent ? ACCENT.amber.text : S[300], color: '#fff', fontSize: 10, fontWeight: 700,
                      }}>{tier.tier}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: S[800] }}>{tier.name}</div>
                        <div style={{ fontSize: 11, color: S[500] }}>
                          {tier.targetType === 'ROLE' ? `Role: ${tier.targetValue}` : tier.resolvedUserName || tier.targetValue}
                          {tier.slaHours ? ` · ${tier.slaHours}h SLA` : ''}
                        </div>
                      </div>
                      {tier.isCurrent && <span style={{ fontSize: 10, fontWeight: 700, color: ACCENT.amber.text }}>CURRENT</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {escalateModal.chain.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', color: S[400], fontSize: 12, marginBottom: 16 }}>
                No escalation policy configured. Task will be routed to the nearest Org Admin.
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: S[600], marginBottom: 4, display: 'block' }}>
                Escalation Reason *
              </label>
              <textarea
                rows={3}
                placeholder="Why is this task being escalated?"
                value={escalateModal.reason}
                onChange={e => setEscalateModal(m => ({ ...m, reason: e.target.value }))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 12,
                  border: `1px solid ${S[200]}`, background: '#fff', resize: 'vertical',
                  outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setEscalateModal({ open: false, taskId: null, chain: [], reason: '' })} style={{
                ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 600,
                background: S[100], color: S[600], border: `1px solid ${S[200]}`,
              }}>
                Cancel
              </button>
              <button disabled={acting || !escalateModal.reason.trim()} onClick={handleEscalate} style={{
                ...btnBase, padding: '9px 18px', fontSize: 12, fontWeight: 700,
                background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff',
                opacity: (acting || !escalateModal.reason.trim()) ? 0.5 : 1,
                cursor: !escalateModal.reason.trim() ? 'not-allowed' : 'pointer',
              }}>
                <AlertTriangle size={13} /> Escalate Now
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 4: CONTROL MAPPING — Comprehensive cross-framework mapping with RBAC
   ══════════════════════════════════════════════════════════════════════════════ */
function ControlMappingTab({ user }) {
  const [canonicalData, setCanonicalData] = useState(null);
  const [frameworkStatuses, setFrameworkStatuses] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subView, setSubView] = useState('overview'); // overview | frameworks | suggestions
  const [scanningFw, setScanningFw] = useState(null);
  const [expandedCanonical, setExpandedCanonical] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const { addToast } = useToast();

  // RBAC context
  const userRole = user?.role || 'Viewer';
  const userOrgId = user?.orgId || null;
  const userTenantId = user?.tenantId || null;
  const isSuperAdmin = ['Super Admin', 'Platform Admin'].includes(userRole);
  const isOrgAdmin = ['Org Admin', 'Super Admin', 'Platform Admin'].includes(userRole);
  const isTenantAdmin = ['Tenant Admin', 'Org Admin', 'Super Admin', 'Platform Admin'].includes(userRole);

  // Org/Tenant selectors for Super Admin
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(isSuperAdmin ? null : userOrgId);
  const [selectedTenantId, setSelectedTenantId] = useState(isSuperAdmin ? null : userTenantId);

  // Load orgs for Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      api.getOrganizations().then(res => { if (res.ok) setOrgs(res.data || []); });
    }
  }, [isSuperAdmin]);

  // Load tenants when org changes
  useEffect(() => {
    if (selectedOrgId) {
      api.getTenantsByOrg(selectedOrgId).then(res => { if (res.ok) setTenants(res.data || []); });
    } else {
      setTenants([]);
      setSelectedTenantId(null);
    }
  }, [selectedOrgId]);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const orgId = isSuperAdmin ? selectedOrgId : userOrgId;
      const tenantId = isSuperAdmin ? selectedTenantId : userTenantId;

      const [canonRes, fwRes, sugRes] = await Promise.all([
        api.ccListCanonicalControls(orgId, tenantId),
        api.ccGetFrameworkMappingStatuses(orgId, tenantId),
        api.ccGetPendingSuggestions(orgId, tenantId),
      ]);

      if (canonRes.ok) setCanonicalData(canonRes.data);
      if (fwRes.ok) setFrameworkStatuses(fwRes.data || []);
      if (sugRes.ok) setSuggestions(sugRes.data || []);
    } catch (e) { addToast('Failed to load control mappings', 'error'); }
    setLoading(false);
  }, [selectedOrgId, selectedTenantId, userOrgId, userTenantId, isSuperAdmin, addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // Actions
  const handleTriggerScan = async (frameworkId) => {
    setScanningFw(frameworkId);
    try {
      const res = await api.ccTriggerMappingScan(frameworkId);
      if (res.ok) {
        const count = res.data?.length || 0;
        addToast(`AI scan complete: ${count} suggestions generated`, 'success');
        loadData();
        if (count > 0) setSubView('suggestions');
      } else {
        addToast('Mapping scan failed', 'error');
      }
    } catch (e) { addToast('Mapping scan failed', 'error'); }
    setScanningFw(null);
  };

  const handleApprove = async (mappingId) => {
    setApprovingId(mappingId);
    try {
      const res = await api.ccApproveMapping(mappingId);
      if (res.ok) {
        addToast('Mapping approved', 'success');
        loadData();
      }
    } catch (e) { addToast('Approve failed', 'error'); }
    setApprovingId(null);
  };

  const handleReject = async (mappingId) => {
    setApprovingId(mappingId);
    try {
      const res = await api.ccRejectMapping(mappingId);
      if (res.ok) {
        addToast('Mapping rejected', 'success');
        loadData();
      }
    } catch (e) { addToast('Reject failed', 'error'); }
    setApprovingId(null);
  };

  if (loading) return <LoadingSpinner />;

  const efficiency = canonicalData?.efficiency || {};
  const canonicalControls = canonicalData?.canonicalControls || [];
  const pendingCount = canonicalData?.pendingSuggestions || 0;
  const mappedCanonicals = canonicalControls.filter(c => c.mappedCount > 0);
  const unmappedCanonicals = canonicalControls.filter(c => c.mappedCount === 0);

  const categoryColor = (cat) => {
    const map = { ACCESS_CONTROL: ACCENT.sky, ENCRYPTION: ACCENT.violet, AUDIT_LOGGING: ACCENT.amber,
      DATA_PROTECTION: ACCENT.emerald, INCIDENT_RESPONSE: ACCENT.rose, CONFIGURATION: ACCENT.slate,
      GOVERNANCE: ACCENT.slate, NETWORK_SECURITY: ACCENT.sky, VULNERABILITY_MGMT: ACCENT.amber,
      RISK_ASSESSMENT: ACCENT.amber, BUSINESS_CONTINUITY: ACCENT.emerald, THIRD_PARTY: ACCENT.violet };
    return map[cat] || ACCENT.slate;
  };

  const mappingTypeLabel = (type) => {
    const map = { IMPLEMENTS: 'Implements', EXCEEDS: 'Exceeds', PARTIALLY_IMPLEMENTS: 'Partial', RELATED_TO: 'Related' };
    return map[type] || type;
  };

  const SUB_VIEWS = [
    { key: 'overview', label: 'Canonical Controls', icon: MapIcon },
    { key: 'frameworks', label: 'Frameworks', icon: Shield },
    { key: 'suggestions', label: `Pending Review${pendingCount > 0 ? ` (${pendingCount})` : ''}`, icon: Sparkles },
  ];

  const labelStyle = { fontSize: 11, fontWeight: 600, color: S[500], marginBottom: 6 };
  const selectStyle = {
    ...inputBase, padding: '8px 12px', fontSize: 13, background: '#fff',
    borderRadius: 8, border: `1px solid ${S[200]}`, cursor: 'pointer',
  };

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>

      {/* ─── RBAC Selectors (Super Admin only) ─── */}
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 200 }}>
            <div style={labelStyle}>Organization</div>
            <select style={selectStyle} value={selectedOrgId || ''} onChange={e => { setSelectedOrgId(e.target.value || null); setSelectedTenantId(null); }}>
              <option value="">All Organizations</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          {selectedOrgId && tenants.length > 0 && (
            <div style={{ minWidth: 200 }}>
              <div style={labelStyle}>Tenant</div>
              <select style={selectStyle} value={selectedTenantId || ''} onChange={e => setSelectedTenantId(e.target.value || null)}>
                <option value="">All Tenants</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {/* ─── Efficiency Stats ─── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Canonical Controls" value={canonicalControls.length} icon={MapIcon} accent={ACCENT.violet} subtitle="Universal control definitions" />
        <StatCard label="Mapped Controls" value={efficiency.totalMappedControls || 0} icon={CheckCircle} accent={ACCENT.emerald} subtitle={`Across ${mappedCanonicals.length} canonical groups`} />
        <StatCard label="Overlap Eliminated" value={efficiency.overlapEliminated || 0} icon={TrendingUp} accent={ACCENT.sky} subtitle={`${(efficiency.overlapPercentage || 0).toFixed(0)}% deduplication`} />
        <StatCard label="Hours Saved" value={efficiency.estimatedHoursSaved || 0} icon={Clock} accent={ACCENT.amber} subtitle="Est. audit time reduction" />
        {pendingCount > 0 && (
          <StatCard label="Pending Review" value={pendingCount} icon={Sparkles} accent={ACCENT.rose} subtitle="AI suggestions awaiting approval" />
        )}
      </div>

      {/* ─── Sub-view Tabs ─── */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `1px solid ${S[200]}`, paddingBottom: 0 }}>
        {SUB_VIEWS.map(sv => {
          const active = subView === sv.key;
          const Icon = sv.icon;
          return (
            <button key={sv.key} onClick={() => setSubView(sv.key)} className="cc-tab" style={{
              ...btnBase, padding: '10px 18px', fontSize: 13, fontWeight: active ? 700 : 500,
              color: active ? BRAND.primary : S[500], background: 'transparent',
              borderBottom: active ? `2px solid ${BRAND.primary}` : '2px solid transparent',
              borderRadius: 0, marginBottom: -1,
            }}>
              <Icon size={14} />
              {sv.label}
            </button>
          );
        })}
      </div>

      {/* ═══ OVERVIEW: Canonical Controls with Cross-Framework Siblings ═══ */}
      {subView === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {canonicalControls.length === 0 ? (
            <EmptyState icon={MapIcon} title="No canonical controls" subtitle="Canonical controls will appear once seeded. These are universal control definitions that map across frameworks." />
          ) : (
            <>
              {/* Mapped canonical controls */}
              {mappedCanonicals.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    Mapped Controls ({mappedCanonicals.length})
                  </div>
                  {mappedCanonicals.map(cc => {
                    const catAccent = categoryColor(cc.category);
                    const isExpanded = expandedCanonical === cc.id;
                    return (
                      <CCCard key={cc.id} accent={catAccent.icon} style={{ padding: 0 }}>
                        <div onClick={() => setExpandedCanonical(isExpanded ? null : cc.id)} style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14 }}>
                          <div style={{
                            width: 38, height: 38, borderRadius: 10, background: catAccent.bg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: `1px solid ${catAccent.border}`, flexShrink: 0,
                          }}>
                            <MapIcon size={18} color={catAccent.icon} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: catAccent.text, fontFamily: "'IBM Plex Mono', monospace" }}>{cc.code}</span>
                              <Badge text={cc.category?.replace(/_/g, ' ')} color={catAccent.text} bg={catAccent.bg} />
                              {cc.automationCapability && <Badge text={cc.automationCapability} color={S[500]} bg={S[100]} />}
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: S[900] }}>{cc.name}</div>
                            {cc.description && <div style={{ fontSize: 12, color: S[500], marginTop: 2, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>{cc.description}</div>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 20, fontWeight: 800, color: BRAND.primary }}>{cc.mappedCount}</div>
                              <div style={{ fontSize: 10, color: S[400], fontWeight: 600 }}>MAPPED</div>
                            </div>
                            {isExpanded ? <ChevronUp size={16} color={S[400]} /> : <ChevronDown size={16} color={S[400]} />}
                          </div>
                        </div>

                        {/* Expanded: show cross-framework siblings */}
                        {isExpanded && cc.mappings && cc.mappings.length > 0 && (
                          <div style={{ borderTop: `1px solid ${S[150]}`, padding: '12px 20px 16px', background: S[25] }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                              Cross-Framework Siblings
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {cc.mappings.map((m, i) => (
                                <div key={m.mappingId || i} className="cc-row" style={{
                                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                  background: '#fff', borderRadius: 8, border: `1px solid ${S[200]}`,
                                }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: 7, background: ACCENT.violet.bg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: `1px solid ${ACCENT.violet.border}`, flexShrink: 0,
                                  }}>
                                    <ArrowRight size={12} color={ACCENT.violet.icon} />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>{m.controlCode}</div>
                                    <div style={{ fontSize: 11, color: S[500], marginTop: 1 }}>{m.controlName}</div>
                                  </div>
                                  <Badge text={m.frameworkName} color={ACCENT.sky.text} bg={ACCENT.sky.bg} />
                                  <Badge text={mappingTypeLabel(m.mappingType)} color={m.mappingType === 'IMPLEMENTS' ? ACCENT.emerald.text : ACCENT.amber.text} bg={m.mappingType === 'IMPLEMENTS' ? ACCENT.emerald.bg : ACCENT.amber.bg} />
                                  <Badge text={`${m.coveragePercentage}%`} color={m.coveragePercentage >= 90 ? ACCENT.emerald.text : ACCENT.amber.text} bg={m.coveragePercentage >= 90 ? ACCENT.emerald.bg : ACCENT.amber.bg} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CCCard>
                    );
                  })}
                </>
              )}

              {/* Unmapped canonical controls */}
              {unmappedCanonicals.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 16, marginBottom: 4 }}>
                    Unmapped Controls ({unmappedCanonicals.length})
                  </div>
                  {unmappedCanonicals.map(cc => {
                    const catAccent = categoryColor(cc.category);
                    return (
                      <CCCard key={cc.id} style={{ padding: '14px 20px', opacity: 0.7 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: 8, background: S[100],
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}>
                            <MapIcon size={16} color={S[400]} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: S[500], fontFamily: "'IBM Plex Mono', monospace" }}>{cc.code}</span>
                              <Badge text={cc.category?.replace(/_/g, ' ')} color={catAccent.text} bg={catAccent.bg} />
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: S[700] }}>{cc.name}</div>
                          </div>
                          <Badge text="No Mappings" color={S[500]} bg={S[100]} />
                        </div>
                      </CCCard>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ FRAMEWORKS: Mapping status per framework with trigger scan ═══ */}
      {subView === 'frameworks' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {frameworkStatuses.length === 0 ? (
            <EmptyState icon={Shield} title="No frameworks found" subtitle="Frameworks with controls will appear here for mapping" />
          ) : (
            frameworkStatuses.map(fw => {
              const pct = fw.totalControls > 0 ? Math.round((fw.approved / fw.totalControls) * 100) : 0;
              const sectorColor = SECTOR_COLORS[fw.sector] || S[500];
              const isScanning = scanningFw === fw.frameworkId;
              return (
                <CCCard key={fw.frameworkId} accent={sectorColor} style={{ padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 42, height: 42, borderRadius: 10,
                      background: `${sectorColor}10`, border: `1px solid ${sectorColor}30`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Shield size={20} color={sectorColor} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: S[900] }}>{fw.name}</span>
                        <Badge text={fw.code} color={S[500]} bg={S[100]} />
                        {fw.sector && <Badge text={fw.sector} color={sectorColor} bg={`${sectorColor}12`} />}
                      </div>

                      {/* Progress bar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                        <div style={{ flex: 1, height: 6, background: S[150], borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 3, transition: 'width 0.3s ease',
                            width: `${pct}%`,
                            background: pct === 100 ? ACCENT.emerald.icon : pct > 50 ? ACCENT.sky.icon : ACCENT.amber.icon,
                          }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: S[600], minWidth: 36 }}>{pct}%</span>
                      </div>

                      {/* Stats */}
                      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                        <span style={{ fontSize: 11, color: S[500] }}><strong style={{ color: ACCENT.emerald.icon }}>{fw.approved}</strong> mapped</span>
                        {fw.pending > 0 && <span style={{ fontSize: 11, color: S[500] }}><strong style={{ color: ACCENT.amber.icon }}>{fw.pending}</strong> pending</span>}
                        <span style={{ fontSize: 11, color: S[500] }}><strong style={{ color: S[600] }}>{fw.unmapped}</strong> unmapped</span>
                        <span style={{ fontSize: 11, color: S[400] }}>{fw.totalControls} total</span>
                      </div>
                    </div>

                    {/* Action: Trigger AI Scan */}
                    {isTenantAdmin && fw.unmapped > 0 && (
                      <button onClick={() => handleTriggerScan(fw.frameworkId)} disabled={isScanning}
                        className="cc-btn-primary" style={{
                          ...btnBase, padding: '9px 16px', fontSize: 12, fontWeight: 600,
                          background: isScanning ? S[300] : BRAND.gradient, color: '#fff', borderRadius: 8,
                          opacity: isScanning ? 0.7 : 1, cursor: isScanning ? 'wait' : 'pointer',
                        }}>
                        {isScanning ? (
                          <><Loader size={13} style={{ animation: 'ccSpin 1s linear infinite' }} /> Scanning...</>
                        ) : (
                          <><Sparkles size={13} /> Map Controls</>
                        )}
                      </button>
                    )}
                    {pct === 100 && (
                      <Badge text="Fully Mapped" color={ACCENT.emerald.text} bg={ACCENT.emerald.bg} />
                    )}
                  </div>
                </CCCard>
              );
            })
          )}
        </div>
      )}

      {/* ═══ SUGGESTIONS: Pending AI suggestions with approve/reject ═══ */}
      {subView === 'suggestions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {suggestions.length === 0 ? (
            <EmptyState icon={Sparkles} title="No pending suggestions" subtitle="Trigger an AI mapping scan from the Frameworks tab to generate suggestions. The AI compares control text across frameworks to find equivalent controls." />
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: S[500], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                {suggestions.length} Pending Suggestion{suggestions.length !== 1 ? 's' : ''} — Review & Approve
              </div>
              {suggestions.map(s => {
                const catAccent = categoryColor(s.category);
                const isProcessing = approvingId === s.mappingId;
                const confidenceColor = s.confidence >= 80 ? ACCENT.emerald : s.confidence >= 60 ? ACCENT.amber : ACCENT.rose;
                return (
                  <CCCard key={s.mappingId} accent={catAccent.icon} style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      {/* Source control */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Framework Control</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>{s.controlCode}</div>
                        <div style={{ fontSize: 11, color: S[500], marginTop: 1 }}>{s.controlName}</div>
                        <div style={{ fontSize: 11, color: S[400], marginTop: 2 }}>{s.frameworkName}</div>
                      </div>

                      {/* Arrow */}
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, background: ACCENT.violet.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `1px solid ${ACCENT.violet.border}`, flexShrink: 0,
                      }}>
                        <ArrowRight size={14} color={ACCENT.violet.icon} />
                      </div>

                      {/* Canonical target */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: S[400], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Canonical Control</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>{s.canonicalCode}</div>
                        <div style={{ fontSize: 11, color: S[500], marginTop: 1 }}>{s.canonicalName}</div>
                        <Badge text={s.category?.replace(/_/g, ' ')} color={catAccent.text} bg={catAccent.bg} />
                      </div>

                      {/* Confidence + mapping type */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0, minWidth: 70 }}>
                        <div style={{
                          fontSize: 18, fontWeight: 800, color: confidenceColor.icon,
                          fontFeatureSettings: "'tnum'",
                        }}>{s.confidence}%</div>
                        <Badge text={mappingTypeLabel(s.mappingType)} color={confidenceColor.text} bg={confidenceColor.bg} />
                      </div>

                      {/* Actions */}
                      {isTenantAdmin && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => handleApprove(s.mappingId)} disabled={isProcessing}
                            className="cc-btn-primary" style={{
                              ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                              background: ACCENT.emerald.bg, color: ACCENT.emerald.text,
                              border: `1px solid ${ACCENT.emerald.border}`, borderRadius: 7,
                              cursor: isProcessing ? 'wait' : 'pointer',
                            }}>
                            <Check size={12} /> Approve
                          </button>
                          <button onClick={() => handleReject(s.mappingId)} disabled={isProcessing}
                            className="cc-btn-ghost" style={{
                              ...btnBase, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                              background: ACCENT.rose.bg, color: ACCENT.rose.text,
                              border: `1px solid ${ACCENT.rose.border}`, borderRadius: 7,
                              cursor: isProcessing ? 'wait' : 'pointer',
                            }}>
                            <X size={12} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </CCCard>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 5: REG CHANGES
   ══════════════════════════════════════════════════════════════════════════════ */
function RegChangesTab() {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.ccListChanges();
        if (res.ok) setChanges(res.data?.changes || []);
      } catch (e) { addToast('Failed to load regulatory changes', 'error'); }
      setLoading(false);
    }
    load();
  }, [addToast]);

  if (loading) return <LoadingSpinner />;

  const changeTypeAccent = (type) => {
    if (type === 'NEW' || type === 'ADDITION') return ACCENT.emerald;
    if (type === 'AMENDMENT' || type === 'UPDATE') return ACCENT.amber;
    if (type === 'REPEAL' || type === 'REMOVAL') return ACCENT.rose;
    return ACCENT.sky;
  };

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      {changes.length === 0 ? (
        <EmptyState icon={GitBranch} title="No recent regulatory changes" subtitle="Regulatory updates and their impact analysis will appear here" />
      ) : (
        <div style={{ position: 'relative', paddingLeft: 24 }}>
          {/* Timeline line */}
          <div style={{ position: 'absolute', left: 11, top: 0, bottom: 0, width: 2, background: S[200], borderRadius: 1 }} />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {changes.map((change, i) => {
              const a = changeTypeAccent(change.changeType);
              return (
                <div key={change.id} style={{ position: 'relative' }}>
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute', left: -18, top: 20, width: 12, height: 12, borderRadius: 6,
                    background: a.icon, border: '2px solid #fff', boxShadow: `0 0 0 2px ${a.border}`,
                  }} />
                  <CCCard accent={a.icon} style={{ padding: 20, marginLeft: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: S[900], marginBottom: 4 }}>{change.framework}</div>
                        <div style={{ fontSize: 12, color: S[500], lineHeight: 1.5, marginBottom: 10 }}>{change.description}</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Badge text={change.changeType} color={a.text} bg={a.bg} />
                          <Badge text={`Effective: ${new Date(change.effectiveDate).toLocaleDateString()}`} />
                          {change.impactLevel && <PriorityBadge priority={change.impactLevel} />}
                        </div>
                      </div>
                    </div>
                  </CCCard>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 6: REPORTS
   ══════════════════════════════════════════════════════════════════════════════ */
function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.ccListReports({});
        if (res.ok) setReports(res.data?.reports || []);
      } catch (e) { addToast('Failed to load reports', 'error'); }
      setLoading(false);
    }
    load();
  }, [addToast]);

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.ccGenerateReport({ reportType: 'COMPREHENSIVE' });
      if (res.ok) {
        addToast('Report generated successfully', 'success');
        const refreshRes = await api.ccListReports({});
        if (refreshRes.ok) setReports(refreshRes.data?.reports || []);
      } else {
        addToast('Failed to generate report', 'error');
      }
    } catch (e) { addToast('Error generating report', 'error'); }
    setGenerating(false);
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const res = await api.ccDownloadReport(reportId);
      if (res.ok) {
        addToast('Report downloaded successfully', 'success');
      } else {
        addToast('Failed to download report', 'error');
      }
    } catch (e) { addToast('Error downloading report', 'error'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      <div style={{ marginBottom: 20 }}>
        <button className="cc-btn-primary" onClick={handleGenerateReport} disabled={generating} style={{
          ...btnBase, padding: '8px 18px', fontSize: 12, fontWeight: 700,
          background: BRAND.gradient, color: '#fff', boxShadow: '0 1px 4px rgba(226,74,58,0.2)',
          opacity: generating ? 0.6 : 1,
        }}><Plus size={14} /> {generating ? 'Generating...' : 'Generate Report'}</button>
      </div>

      {reports.length === 0 ? (
        <EmptyState icon={FileText} title="No reports yet" subtitle="Generate your first compliance report to see it here" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {reports.map(report => (
            <CCCard key={report.id} accent={ACCENT.sky.icon} style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: ACCENT.sky.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${ACCENT.sky.border}`, flexShrink: 0,
                }}>
                  <FileText size={18} color={ACCENT.sky.icon} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: S[900], marginBottom: 4 }}>{report.name}</div>
                  <div style={{ fontSize: 11, color: S[500] }}>Generated {new Date(report.createdAt).toLocaleDateString()}</div>
                  {report.format && <Badge text={report.format} />}
                </div>
                <button className="cc-btn-ghost" onClick={() => handleDownloadReport(report.id)} style={{
                  ...btnBase, padding: '7px 12px', fontSize: 12, fontWeight: 500,
                  background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                }}><Download size={13} /></button>
              </div>
            </CCCard>
          ))}
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 7: AI ASSISTANT
   ══════════════════════════════════════════════════════════════════════════════ */
function AIAssistantTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const newMsg = { id: Date.now(), role: 'user', text: input };
    setMessages(prev => [...prev, newMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await api.ccAnalyzeControl({ query: input });
      if (res.ok) {
        setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: res.data?.response || '' }]);
      }
    } catch (e) { addToast('AI Assistant error', 'error'); }
    setLoading(false);
  };

  const suggestions = [
    'What is our overall compliance posture?',
    'Which agents are at highest risk?',
    'Summarize gaps in our SOC2 coverage',
    'What remediations are overdue?',
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, animation: 'ccFadeIn 0.3s ease' }}>
      <CCCard style={{ flex: 1, padding: 0, overflowY: 'auto', minHeight: 360, display: 'flex', flexDirection: 'column' }}>
        {messages.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, background: BRAND.gradientSubtle,
              border: `1px solid ${S[200]}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
            }}>
              <Sparkles size={24} color={BRAND.primary} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: S[900], marginBottom: 4 }}>Compliance AI Assistant</div>
            <div style={{ fontSize: 12, color: S[500], marginBottom: 20 }}>Ask questions about your compliance posture, controls, and risks</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 500 }}>
              {suggestions.map(s => (
                <button key={s} onClick={async () => {
                  const newMsg = { id: Date.now(), role: 'user', text: s };
                  setMessages(prev => [...prev, newMsg]);
                  setLoading(true);
                  try {
                    const res = await api.ccAnalyzeControl({ query: s });
                    if (res.ok) {
                      setMessages(prev => [...prev, { id: Date.now(), role: 'assistant', text: res.data?.response || '' }]);
                    }
                  } catch (e) { addToast('AI Assistant error', 'error'); }
                  setLoading(false);
                }} style={{
                  ...btnBase, padding: '6px 14px', fontSize: 12, fontWeight: 500,
                  background: S[50], color: S[600], border: `1px solid ${S[200]}`, borderRadius: 20,
                }}>{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map(msg => (
              <div key={msg.id} style={{
                padding: '12px 16px', borderRadius: 12, maxWidth: '80%', lineHeight: 1.5, fontSize: 13,
                background: msg.role === 'user' ? BRAND.gradient : S[50],
                color: msg.role === 'user' ? '#fff' : S[800],
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                border: msg.role === 'user' ? 'none' : `1px solid ${S[200]}`,
                animation: 'ccSlideUp 0.2s ease',
              }}>
                {msg.role === 'assistant' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                    <Sparkles size={12} color={BRAND.primary} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: BRAND.primary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>AI Assistant</span>
                  </div>
                )}
                {msg.text}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: 'flex-start', padding: '12px 16px', borderRadius: 12, background: S[50], border: `1px solid ${S[200]}` }}>
                <Loader size={14} style={{ animation: 'ccSpin 1s linear infinite', color: BRAND.primary }} />
              </div>
            )}
          </div>
        )}
      </CCCard>

      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text" value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Ask a compliance question..."
          className="cc-search"
          style={{ ...inputBase, flex: 1, borderRadius: 12, padding: '11px 16px' }}
        />
        <button className="cc-btn-primary" onClick={handleSendMessage} disabled={loading} style={{
          ...btnBase, padding: '10px 20px', fontSize: 12, fontWeight: 700,
          background: BRAND.gradient, color: '#fff', borderRadius: 12,
          boxShadow: '0 1px 4px rgba(226,74,58,0.2)', opacity: loading ? 0.6 : 1,
        }}><Send size={14} /></button>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 8: NOTIFICATIONS
   ══════════════════════════════════════════════════════════════════════════════ */
function NotificationsTab() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('HISTORY');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.ccGetNotifications(unreadOnly);
        if (res.ok) setNotifications(res.data?.notifications || []);
        const prefsRes = await api.ccGetNotificationPrefs();
        if (prefsRes.ok) setPrefs(prefsRes.data || {});
      } catch (e) { addToast('Failed to load notifications', 'error'); }
      setLoading(false);
    }
    load();
  }, [unreadOnly, addToast]);

  const handleMarkRead = async (ids) => {
    try {
      await api.ccMarkNotificationsRead(ids);
      setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
    } catch (e) { addToast('Error marking as read', 'error'); }
  };

  const handleMarkAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (unreadIds.length > 0) await handleMarkRead(unreadIds);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.ccUpdateNotificationPrefs(prefs);
      addToast('Preferences saved', 'success');
    } catch (e) { addToast('Failed to save preferences', 'error'); }
    setSaving(false);
  };

  const notificationTypes = [
    { key: 'DRIFT_ALERTS', label: 'Drift Alerts', desc: 'Alerts when configuration drift is detected', icon: TrendingUp, accent: ACCENT.amber },
    { key: 'REMEDIATION_OVERDUE', label: 'Remediation Overdue', desc: 'Reminders for overdue remediation tasks', icon: Wrench, accent: ACCENT.rose },
    { key: 'ATTESTATION_DUE', label: 'Attestation Due', desc: 'Notifications for upcoming attestation deadlines', icon: FileCheck, accent: ACCENT.sky },
    { key: 'REPORT_READY', label: 'Report Ready', desc: 'Notifications when compliance reports are generated', icon: FileText, accent: ACCENT.emerald },
  ];

  if (loading) return <LoadingSpinner />;

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      <SubViewToggle
        views={[
          { key: 'HISTORY', label: 'Notifications', icon: Inbox, count: unreadCount },
          { key: 'PREFERENCES', label: 'Preferences', icon: Settings },
        ]}
        active={activeView}
        onChange={setActiveView}
      />

      {activeView === 'HISTORY' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: S[500], display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} style={{ width: 14, height: 14, cursor: 'pointer', accentColor: BRAND.primary }} />
              Unread only
            </label>
            {unreadCount > 0 && (
              <button className="cc-btn-ghost" onClick={handleMarkAllRead} style={{
                ...btnBase, padding: '6px 14px', fontSize: 12, fontWeight: 500,
                background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
              }}><Check size={13} /> Mark all read</button>
            )}
          </div>

          {notifications.length === 0 ? (
            <EmptyState icon={Bell} title="No notifications" subtitle="You're all caught up" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {notifications.map(n => (
                <CCCard key={n.id} style={{
                  padding: 16, borderLeft: `3px solid ${n.read ? S[200] : BRAND.primary}`,
                  background: n.read ? S[25] : '#fff',
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: n.read ? S[100] : BRAND.light,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1px solid ${n.read ? S[200] : BRAND.soft}`,
                    }}>
                      <Bell size={16} color={n.read ? S[400] : BRAND.primary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: S[900] }}>{n.title || n.type}</span>
                        {!n.read && <span style={{ width: 7, height: 7, borderRadius: 4, background: BRAND.primary, flexShrink: 0 }} />}
                      </div>
                      <div style={{ fontSize: 12, color: S[600], lineHeight: 1.5 }}>{n.message || n.body}</div>
                      <div style={{ fontSize: 11, color: S[400], marginTop: 6 }}>
                        {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                      </div>
                    </div>
                    {!n.read && (
                      <button className="cc-btn-ghost" onClick={() => handleMarkRead([n.id])} style={{
                        ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 500,
                        background: 'transparent', color: S[500], border: `1px solid ${S[200]}`,
                      }}>Read</button>
                    )}
                  </div>
                </CCCard>
              ))}
            </div>
          )}
        </div>
      )}

      {activeView === 'PREFERENCES' && (
        <div>
          {notificationTypes.map(nt => {
            const Icon = nt.icon;
            return (
              <CCCard key={nt.key} accent={nt.accent.icon} style={{ marginBottom: 12, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flex: 1 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, background: nt.accent.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1px solid ${nt.accent.border}`, flexShrink: 0,
                    }}>
                      <Icon size={16} color={nt.accent.icon} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 13, fontWeight: 700, color: S[900], margin: '0 0 4px 0' }}>{nt.label}</h3>
                      <p style={{ margin: 0, fontSize: 12, color: S[500], lineHeight: 1.5 }}>{nt.desc}</p>
                    </div>
                  </div>
                  <div style={{ position: 'relative', width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s', background: prefs[nt.key]?.enabled !== false ? BRAND.primary : S[300] }} onClick={() => setPrefs({ ...prefs, [nt.key]: { ...prefs[nt.key], enabled: !(prefs[nt.key]?.enabled !== false) } })}>
                    <div style={{
                      position: 'absolute', top: 2, left: prefs[nt.key]?.enabled !== false ? 22 : 2,
                      width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    }} />
                  </div>
                </div>
              </CCCard>
            );
          })}

          <div style={{ marginTop: 20 }}>
            <button className="cc-btn-primary" onClick={handleSave} disabled={saving} style={{
              ...btnBase, padding: '9px 20px', fontSize: 12, fontWeight: 700,
              background: BRAND.gradient, color: '#fff', boxShadow: '0 1px 4px rgba(226,74,58,0.2)',
              opacity: saving ? 0.6 : 1,
            }}>{saving ? 'Saving...' : 'Save Preferences'}</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   TAB 9: CERTIFICATES
   ══════════════════════════════════════════════════════════════════════════════ */
function CertificatesTab() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingType, setGeneratingType] = useState(null);
  const { addToast } = useToast();

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getComplianceCertificates();
        if (res.ok) setCertificates(Array.isArray(res.data) ? res.data : res.data?.content || []);
      } catch (e) { addToast('Failed to load certificates', 'error'); }
      setLoading(false);
    }
    load();
  }, [addToast]);

  const handleGenerateCertificate = async (certKey, certLabel) => {
    setGeneratingType(certKey);
    try {
      const res = await api.getComplianceCertificates({ type: certKey });
      if (res.ok) {
        addToast(`${certLabel} certificate generated successfully`, 'success');
        const refreshRes = await api.getComplianceCertificates();
        if (refreshRes.ok) setCertificates(Array.isArray(refreshRes.data) ? refreshRes.data : refreshRes.data?.content || []);
      } else {
        addToast(`Failed to generate ${certLabel} certificate`, 'error');
      }
    } catch (e) { addToast(`Error generating ${certLabel} certificate`, 'error'); }
    setGeneratingType(null);
  };

  if (loading) return <LoadingSpinner />;

  const certTypes = [
    { key: 'SOC2', label: 'SOC 2 Type II', desc: 'Service Organization Control audit', accent: ACCENT.sky },
    { key: 'HIPAA', label: 'HIPAA', desc: 'Health data privacy compliance', accent: ACCENT.emerald },
    { key: 'ISO27001', label: 'ISO 27001', desc: 'Information security management', accent: ACCENT.violet },
  ];

  return (
    <div style={{ animation: 'ccFadeIn 0.3s ease' }}>
      <CCCard style={{ marginBottom: 24, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Award size={15} color={BRAND.primary} />
          <h3 style={{ fontSize: 13, fontWeight: 700, color: S[900], margin: 0 }}>Generate Compliance Certificate</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {certTypes.map(cert => (
            <button key={cert.key} className="cc-card" onClick={() => handleGenerateCertificate(cert.key, cert.label)} disabled={generatingType === cert.key} style={{
              padding: 20, borderRadius: 12, border: `1px solid ${cert.accent.border}`, cursor: generatingType === cert.key ? 'default' : 'pointer',
              textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
              background: cert.accent.bg, transition: 'all 0.2s', opacity: generatingType === cert.key ? 0.6 : 1,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, background: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1px solid ${cert.accent.border}`,
              }}>
                {generatingType === cert.key ? (
                  <Loader size={20} style={{ animation: 'ccSpin 1s linear infinite', color: cert.accent.icon }} />
                ) : (
                  <Award size={20} color={cert.accent.icon} />
                )}
              </div>
              <div style={{ fontWeight: 700, fontSize: 14, color: cert.accent.text }}>{generatingType === cert.key ? 'Generating...' : cert.label}</div>
              <div style={{ fontSize: 11, color: S[500] }}>{cert.desc}</div>
            </button>
          ))}
        </div>
      </CCCard>

      {certificates.length === 0 ? (
        <EmptyState icon={Award} title="No certificates generated" subtitle="Generate compliance certificates for your assigned frameworks" />
      ) : (
        <CCCard style={{ overflow: 'hidden' }}>
          <DataTable
            columns={[
              { key: 'framework', label: 'Framework' },
              { key: 'type', label: 'Type' },
              { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> },
              { key: 'issuedAt', label: 'Issued', render: (row) => new Date(row.issuedAt).toLocaleDateString() },
              { key: 'expiresAt', label: 'Expires', render: (row) => {
                const isExpiringSoon = new Date(row.expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                return <span style={{ color: isExpiringSoon ? ACCENT.amber.icon : S[600], fontWeight: isExpiringSoon ? 600 : 400 }}>{new Date(row.expiresAt).toLocaleDateString()}</span>;
              }},
              { key: 'actions', label: '', render: () => (
                <button className="cc-btn-ghost" style={{
                  ...btnBase, padding: '5px 12px', fontSize: 11, fontWeight: 500,
                  background: 'transparent', color: S[600], border: `1px solid ${S[200]}`,
                }}><Download size={12} /> Download</button>
              )},
            ]}
            rows={certificates}
          />
        </CCCard>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT — Compliance Center
   ══════════════════════════════════════════════════════════════════════════════ */
export default function ComplianceCenterPage({ agentScope = null }) {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('DASHBOARD');

  const scopeParam = searchParams.get('scope');
  const agentType = scopeParam === 'native' ? 'NATIVE' : scopeParam === 'external' ? 'EXTERNAL' : null;

  if (!user || !['Viewer', 'Compliance Admin', 'Tenant Admin', 'Org Admin', 'Platform Admin', 'Super Admin'].includes(user.role)) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ padding: '16px 24px', background: ACCENT.rose.bg, color: ACCENT.rose.text, borderRadius: 10, border: `1px solid ${ACCENT.rose.border}`, display: 'inline-block' }}>
          You do not have permission to access this page
        </div>
      </div>
    );
  }

  /* G104 — v2 TabNav config + active-tab description */
  const v2Tabs = TABS.map(t => ({ id: t.key, label: t.label }));
  const activeTabMeta = TABS.find(t => t.key === activeTab);

  return (
    <V2PageShell
      title="Compliance Center"
      subtitle="Enterprise compliance monitoring, attestations, and regulatory change management."
      tags={<V2Pill tone="cyan" size="sm">Protect</V2Pill>}
      actions={
        <div style={{ display: 'flex', gap: 2, background: S[100], borderRadius: 8, padding: 2 }}>
          {[
            { key: null, label: 'All Agents' },
            { key: 'native', label: 'Native' },
            { key: 'external', label: 'External' },
          ].map(s => {
            const isActive = scopeParam === s.key || (s.key === null && !scopeParam);
            return (
              <button key={s.key || 'all'} onClick={() => {
                const newParams = new URLSearchParams(searchParams);
                if (s.key) { newParams.set('scope', s.key); } else { newParams.delete('scope'); }
                setSearchParams(newParams);
              }} style={{
                ...btnBase, padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: isActive ? 700 : 500,
                background: isActive ? '#fff' : 'transparent',
                color: isActive ? '#18181b' : S[500],
                boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}>
                {s.label}
              </button>
            );
          })}
        </div>
      }
    >
      <GlobalStyles />

      <V2TabNav
        tabs={v2Tabs}
        activeId={activeTab}
        onChange={setActiveTab}
        ariaLabel="Compliance Center sections"
      />

      {/* Active tab description (kept from v1) */}
      {activeTabMeta && (
        <div className="px-3 py-2 mt-3 mb-4 rounded-v2-sm border border-v2-border-subtle bg-v2-muted text-[12px] text-v2-ink-secondary flex items-center gap-2">
          {React.createElement(activeTabMeta.icon, { size: 12 })}
          <span>{activeTabMeta.desc}</span>
        </div>
      )}

      {/* Tab content */}
      <div className="overflow-y-auto">
        {activeTab === 'DASHBOARD' && <DashboardTab agentScope={agentScope} agentType={agentType} />}
        {activeTab === 'ATTESTATIONS' && <AttestationsTab />}
        {activeTab === 'REMEDIATION' && <RemediationTab />}
        {activeTab === 'CONTROL_MAPPING' && <ControlMappingTab user={user} />}
        {activeTab === 'REG_CHANGES' && <RegChangesTab />}
        {activeTab === 'REPORTS' && <ReportsTab />}
        {activeTab === 'AI_ASSISTANT' && <AIAssistantTab />}
        {activeTab === 'NOTIFICATIONS' && <NotificationsTab />}
        {activeTab === 'CERTIFICATES' && <CertificatesTab />}
      </div>
    </V2PageShell>
  );
}
